import React, { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import Draggable from "react-draggable";
import { X, Trash2, Download } from "lucide-react";
import './DXFViewer2D.css';
import DXFLoader from "./DXFLoader";
import PdfExportPanel from "../PdfExportPanel";
import { getAuthHeaders } from '../../utils/auth';

// ═══════════════════════════════════════════════════════════════════════════
//  GÉNÉRATEUR DXF CLIENT-SIDE  (R2010 — ASCII)
//  Parcourt la scène Three.js et exporte toutes les entités avec userData.
// ═══════════════════════════════════════════════════════════════════════════

function generateDXF(scene, layersByName = {}) {
  const out = [];
  const ln  = (...args) => args.forEach(a => out.push(String(a)));
  const f6  = v => (typeof v === "number" ? v : 0).toFixed(6);

  // ── HEADER ────────────────────────────────────────────────────────────────
  ln("0","SECTION","2","HEADER");
  ln("9","$ACADVER","1","AC1015");
  ln("9","$INSUNITS","70","6");   // 6 = mètres
  ln("0","ENDSEC");

  // ── TABLES (calques) ──────────────────────────────────────────────────────
  ln("0","SECTION","2","TABLES");
  ln("0","TABLE","2","LAYER","70", Object.keys(layersByName).length || 1);
  const layerNames = Object.keys(layersByName).length
    ? Object.keys(layersByName)
    : ["0"];
  layerNames.forEach(name => {
    ln("0","LAYER","2",name,"70","0","62","7","6","CONTINUOUS");
  });
  ln("0","ENDTAB","0","ENDSEC");

  // ── ENTITÉS ───────────────────────────────────────────────────────────────
  ln("0","SECTION","2","ENTITIES");

  scene.traverse(obj => {
    const ud    = obj.userData;
    const layer = ud?.layer || "0";

    // LINE
    if (ud?.type === "LINE" && obj.geometry) {
      const pos = obj.geometry.getAttribute("position");
      if (!pos || pos.count < 2) return;
      ln("0","LINE","8",layer,
         "10",f6(pos.getX(0)),"20",f6(pos.getY(0)),"30","0.0",
         "11",f6(pos.getX(1)),"21",f6(pos.getY(1)),"31","0.0");
    }

    // CIRCLE — on lit les métadonnées (center/radius stockés dans userData)
    else if (ud?.type === "CIRCLE" && ud.center && ud.radius != null) {
      ln("0","CIRCLE","8",layer,
         "10",f6(ud.center[0]),"20",f6(ud.center[1]),"30","0.0",
         "40",f6(ud.radius));
    }

    // ARC — angles en degrés dans le fichier DXF
    else if (ud?.type === "ARC" && ud.center && ud.radius != null) {
      const toDeg = r => ((r * 180) / Math.PI).toFixed(6);
      ln("0","ARC","8",layer,
         "10",f6(ud.center[0]),"20",f6(ud.center[1]),"30","0.0",
         "40",f6(ud.radius),
         "50",toDeg(ud.startAngle ?? 0),
         "51",toDeg(ud.endAngle   ?? Math.PI * 2));
    }

    // POLYLINE / LWPOLYLINE
    else if ((ud?.type === "POLYLINE" || ud?.type === "LWPOLYLINE") && obj.geometry) {
      const pos    = obj.geometry.getAttribute("position");
      if (!pos || pos.count < 2) return;
      const closed = obj instanceof THREE.LineLoop ? 1 : 0;
      // LineLoop duplique le premier point en dernier → on l'ignore
      const n = closed ? Math.max(0, pos.count - 1) : pos.count;
      ln("0","LWPOLYLINE","8",layer,
         "90",n,"70",closed);
      for (let i = 0; i < n; i++) {
        ln("10",f6(pos.getX(i)),"20",f6(pos.getY(i)));
      }
    }

    // SPLINE → convertie en POLYLINE approchée (points de contrôle)
    else if (ud?.type === "SPLINE" && obj.geometry) {
      const pos = obj.geometry.getAttribute("position");
      if (!pos || pos.count < 2) return;
      ln("0","LWPOLYLINE","8",layer,"90",pos.count,"70","0");
      for (let i = 0; i < pos.count; i++) {
        ln("10",f6(pos.getX(i)),"20",f6(pos.getY(i)));
      }
    }
  });

  ln("0","ENDSEC","0","EOF");
  return out.join("\n");
}

// ── Styles partagés — thème dark identique au PdfExportPanel ────────────────
const DW = {
  wrap: {
    position: "absolute", top: 100, left: 100,
    width: 270,
    background: "#1a2236",
    border: "1px solid #2d3a55",
    borderRadius: 8,
    boxShadow: "0 8px 32px rgba(0,0,0,0.7)",
    fontFamily: "Segoe UI, sans-serif",
    userSelect: "none",
    zIndex: 2000,
    overflow: "hidden",
  },
  header: (accentColor) => ({
    background: "#0f1729",
    borderBottom: "1px solid #2d3a55",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "grab",
  }),
  headerTitle: (accentColor) => ({
    fontWeight: 700,
    fontSize: 13,
    color: accentColor ?? "#7aa6d4",
    letterSpacing: "0.01em",
  }),
  closeBtn: {
    background: "transparent",
    border: "none",
    color: "#555",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    transition: "color .12s",
  },
  body: { padding: "12px 14px" },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    gap: 8,
  },
  label: { fontSize: 11, fontWeight: 700, color: "#7aa6d4", textTransform: "uppercase", letterSpacing: ".08em", flexShrink: 0 },
  value: { fontSize: 12, color: "#ddd", fontFamily: "Consolas, monospace", textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  sep: { borderTop: "1px solid #2d3a55", margin: "8px 0" },
  input: {
    width: "100%", boxSizing: "border-box",
    background: "#2d3a55", color: "#ddd",
    border: "1px solid #4a5a80", borderRadius: 4,
    padding: "5px 8px", fontSize: 12,
    outline: "none",
    fontFamily: "Consolas, monospace",
  },
  btnPrimary: {
    width: "100%", padding: "7px 0", marginTop: 10,
    background: "linear-gradient(135deg,#1e3a5f,#2a527a)",
    border: "1px solid #4a9eff", color: "#4a9eff",
    borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600,
    transition: "opacity .12s",
  },
  btnDanger: {
    width: "100%", padding: "7px 0", marginTop: 8,
    background: "rgba(200,60,40,0.18)",
    border: "1px solid rgba(200,80,60,0.5)", color: "#e07060",
    borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600,
  },
};

export function PropertiesWindow({ selected, onClose, onExplode }) {
  const nodeRef = React.useRef(null);
  if (!selected) return null;

  const isInsert = selected.type === "INSERT";
  const accent   = isInsert ? "#b08fff" : "#4a9eff";

  const rows = [
    { label: "Type",    value: isInsert ? "INSERT (bloc)" : selected.type },
    { label: "Calque",  value: selected.layer },
    ...(isInsert && selected.name     ? [{ label: "Nom",      value: selected.name }] : []),
    ...(isInsert && selected.position ? [{ label: "Position", value: `${selected.position[0].toFixed(3)}, ${selected.position[1].toFixed(3)}` }] : []),
    ...(isInsert && selected.rotation !== undefined ? [{ label: "Rotation", value: `${selected.rotation}°` }] : []),
    ...(isInsert && selected.scale    ? [{ label: "Échelle",  value: `${selected.scale[0]} × ${selected.scale[1]}` }] : []),
    ...(selected.area !== undefined   ? [{ label: "Surface",  value: `${selected.area.toFixed(3)} m²` }] : []),
  ];

  return (
    <Draggable nodeRef={nodeRef} handle=".window-header">
      <div ref={nodeRef} style={{ ...DW.wrap, top: 100, left: 100 }}>
        <div className="window-header" style={DW.header()}>
          <span style={DW.headerTitle(accent)}>
            {isInsert ? `⬛ Bloc : ${selected.name}` : "⬡ Propriétés"}
          </span>
          <button onClick={onClose} style={DW.closeBtn} title="Fermer">
            <X size={15} />
          </button>
        </div>

        <div style={DW.body}>
          {rows.map(({ label, value }) => (
            <div key={label} style={DW.row}>
              <span style={DW.label}>{label}</span>
              <span style={{ ...DW.value, color: accent === "#b08fff" && label === "Nom" ? "#b08fff" : "#ddd" }}>
                {value}
              </span>
            </div>
          ))}

          {isInsert && onExplode && (
            <>
              <div style={DW.sep} />
              <button onClick={onExplode} style={DW.btnDanger}>
                ⚡ Exploser le bloc
              </button>
            </>
          )}
        </div>
      </div>
    </Draggable>
  );
}


export function XYSearchWindow({ onSearch, onClose }) {
  const nodeRef = useRef(null);
  const [x, setX] = useState("");
  const [y, setY] = useState("");
  const [err, setErr] = useState("");

  const handleSearch = () => {
    const nx = parseFloat(x);
    const ny = parseFloat(y);
    if (!isNaN(nx) && !isNaN(ny)) {
      setErr("");
      onSearch(nx, ny);
    } else {
      setErr("Entrez des valeurs numériques valides.");
    }
  };

  return (
    <Draggable nodeRef={nodeRef} handle=".window-header">
      <div ref={nodeRef} style={{ ...DW.wrap, top: 120, left: 120 }}>
        <div className="window-header" style={DW.header()}>
          <span style={DW.headerTitle()}>⊕ Naviguer vers XY</span>
          <button onClick={onClose} style={DW.closeBtn} title="Fermer">
            <X size={15} />
          </button>
        </div>

        <div style={DW.body}>
          {[["X", x, setX], ["Y", y, setY]].map(([lbl, val, set]) => (
            <div key={lbl} style={{ marginBottom: 8 }}>
              <div style={{ ...DW.label, marginBottom: 4 }}>{lbl}</div>
              <input
                type="number"
                value={val}
                onChange={e => { set(e.target.value); setErr(""); }}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
                placeholder={`Coordonnée ${lbl}…`}
                style={DW.input}
              />
            </div>
          ))}

          {err && (
            <div style={{ fontSize: 11, color: "#e07060", marginTop: 4, marginBottom: 2 }}>
              ⚠ {err}
            </div>
          )}

          <button onClick={handleSearch} style={DW.btnPrimary}>
            ⊕ Aller aux coordonnées
          </button>
        </div>
      </div>
    </Draggable>
  );
}


export default function DXFViewer() {
  // =========================================================
  //  REFS – mutables, jamais de re-render
  // =========================================================
  const mountRef      = useRef(null);
  const overlayRef    = useRef(null);   // canvas 2D (snap + rubber-band)
  const sceneRef      = useRef(null);
  const cameraRef     = useRef(null);
  const rendererRef   = useRef(null);
  const layersByNameRef = useRef({});   // { layerName: { group, color } }

  // Outil actif (ref pour event handlers sans re-render)
  const selectedToolRef  = useRef("select");
  const activeLayerRef   = useRef("0");

  // Dessin
  const drawingPtsRef    = useRef([]);  // points accumulés
  const isDrawingRef     = useRef(false);
  const previewPtRef     = useRef(null); // position souris (world)
  const snapPtRef        = useRef(null); // point d'accrochage actif

  // Sélection
  const selStartRef      = useRef(null);  // { x, y } écran
  const selBoxElRef      = useRef(null);  // div DOM de la boîte
  const isDraggingRef    = useRef(false);
  const selectedObjsRef  = useRef([]);

  // Pan
  const isPanCtrlRef     = useRef(false);
  const panCtrlStartRef  = useRef({ x: 0, y: 0 }); 
  const isPanMidRef      = useRef(false);
  const panMidStartRef   = useRef({ x: 0, y: 0 });

  // Objets texte dessinés (rendu sur overlay canvas)
  const textObjectsRef   = useRef([]);  // [{ text, x, y, color, size }]

  // Hachures dessinées
  const hatchObjectsRef  = useRef([]);  // [{ boundary:[{x,y}], angle, spacing, color }]
  const hatchPreviewRef  = useRef(null); // polygone survolé en mode hachure

  // DXF chargé depuis le backend
  const renderDXFRef   = useRef(null);       // fn(data) → charge les entités dans la scène
  const dxfCenterRef   = useRef([0, 0, 0]);  // centre du dernier DXF chargé
  const dxfDataRef     = useRef(null);       // données brutes complètes (pour PDF export)

  // Cotation
  const dimObjectsRef    = useRef([]);          // cotations validées
  const dimStyleRef      = useRef(null);        // style courant (synced from state)
  const dimArcRef        = useRef(null);        // arc/cercle sélectionné pour cote radiale
  const redrawOverlayRef = useRef(null);        // exposé pour déclencher redraw hors closure

  // Hachure
  const hatchStyleRef  = useRef(null);        // style hachure courant (synced from state)

  // Blocs (registre + insertion)
  const blockDefsRef        = useRef(new Map()); // blockName → { type:"entity_data"|"three_clone", data }
  const insertingBlockRef   = useRef(null);       // nom du bloc en cours d'insertion (string ou null)
  const createBlockGhostRef = useRef(null);       // fn(name) → crée le fantôme THREE.js
  const removeBlockGhostRef = useRef(null);       // fn() → retire le fantôme
  const explodeInsertRef    = useRef(null);       // fn(threeGroup) → exploser un INSERT
  const createBlockFromSelRef = useRef(null);     // fn(name) → créer bloc depuis sélection

  // =========================================================
  //  STATE – uniquement pour l'UI React
  // =========================================================
  const [zoom, setZoom]                   = useState(1);
  const [layers, setLayers]               = useState([]);
  const [activeLayer, setActiveLayer]     = useState("0");
  const [selectedTool, setSelectedTool]   = useState("select");
  const [showLayers, setShowLayers]       = useState(false);
  const [showXYSearch, setShowXYSearch]   = useState(false);
  const [showRadialMenu, setShowRadialMenu] = useState(false);
  const [showFileMenu, setShowFileMenu]   = useState(false);
  const [cursor, setCursor]               = useState({ x: 0, y: 0 });
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [statusMsg, setStatusMsg]         = useState("Prêt - Cliquer pour sélectionner");
  const [snapLabel, setSnapLabel]         = useState("");
  // Saisie texte flottante (outil TEXTE)
  const [textInput, setTextInput]         = useState(null); // { sx, sy, wx, wy }
  const [textValue, setTextValue]         = useState("");
  const [dxfLoading, setDxfLoading]       = useState(false);
  const [showPdfPanel, setShowPdfPanel]   = useState(false);
  const [hasFile, setHasFile]             = useState(false);   // DXF chargé
  const [dropActive, setDropActive]       = useState(false);   // drag-over sur la drop zone
  const [loadedFileName, setLoadedFileName] = useState("");    // nom du fichier chargé
  const fileInputRef = useRef(null);

  // Ref exposant deleteSelected au handler clavier (closure unique dans useEffect)
  const deleteSelectedRef = useRef(null);

  // Blocs – UI
  const [showBlockPanel, setShowBlockPanel]   = useState(false);
  const [blockNames, setBlockNames]           = useState([]);      // noms disponibles
  const [insertingBlock, setInsertingBlock]   = useState(null);    // nom du bloc actif
  const [createBlockOpen, setCreateBlockOpen] = useState(false);
  const [newBlockName, setNewBlockName]       = useState("");

  // Style de cotation
  const [dimStyle, setDimStyle] = useState({
    textSize:  0.3,
    arrowSize: 0.25,
    color:     "#ffcc00",
    lineGap:   0.05,
    precision: 3,
  });
  const [showDimConfig, setShowDimConfig] = useState(false);
  const [configTab,     setConfigTab]     = useState("cotes"); // "cotes" | "hachure"

  // Style de hachure
  const [hatchStyle, setHatchStyle] = useState({
    pattern:      "lines",   // lines | cross | square | diamond | dot | zigzag | honeycomb | brick
    angle:        45,
    spacing:      0.3,
    opacity:      0.75,
    useLayerColor: true,
    color:        "#00aaff",
    snapTol:      0.05,      // tolérance de jonction (unités monde) pour segments décomposés
  });

  // =========================================================
  //  SYNC outil → ref + status bar
  // =========================================================
  useEffect(() => {
    selectedToolRef.current = selectedTool;
    // Annuler tout dessin en cours quand on change d'outil
    drawingPtsRef.current  = [];
    isDrawingRef.current   = false;
    previewPtRef.current   = null;
    snapPtRef.current      = null;
    hatchPreviewRef.current = null;
    setSnapLabel("");
    // Effacer l'overlay
    const canvas = overlayRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    const msgs = {
      select:         "SELECTION – Cliquer pour sélectionner | Glisser G→D = fenêtre | Glisser D→G = croisement",
      line:           "LIGNE – Spécifier le premier point",
      polyline:       "POLYLIGNE – Spécifier le premier point",
      circle:         "CERCLE – Spécifier le centre",
      rectangle:      "RECTANGLE – Spécifier le premier coin",
      hatch:          "HACHURE – Cliquer à l'intérieur d'une zone fermée",
      text:           "TEXTE – Cliquer l'emplacement du texte",
      dim_linear:     "COT. LINÉAIRE – Spécifier le premier point de mesure",
      dim_aligned:    "COT. ALIGNÉE – Spécifier le premier point de mesure",
      dim_angular:    "COT. ANGULAIRE – Cliquer le sommet de l'angle",
      dim_radial:     "COT. RAYON – Cliquer sur l'arc ou le cercle",
      dim_diameter:   "COT. DIAMÈTRE – Cliquer sur le cercle",
      dim_arc_length: "LONG. D'ARC – Cliquer sur l'arc",
      dim_ordinate:   "ORDONNÉE – Cliquer le point à coter",
      dim_jogged:     "COT. INCLINÉE – Cliquer sur le cercle ou l'arc",
    };
    setStatusMsg(msgs[selectedTool] || "Prêt");
  }, [selectedTool]);

  useEffect(() => { activeLayerRef.current = activeLayer; }, [activeLayer]);
  useEffect(() => {
    dimStyleRef.current = dimStyle;
    redrawOverlayRef.current?.();   // re-render toutes les cotations avec le nouveau style
  }, [dimStyle]);
  useEffect(() => { hatchStyleRef.current = hatchStyle; }, [hatchStyle]);

  // =========================================================
  //  IMPORT FICHIER DXF  →  backend /api/topo_manager/upload-dxf/
  // =========================================================
  // ── Logique d'import centralisée (fichier input OU drag-and-drop) ────────
  const importDXF = async (file) => {
    if (!file) return;
    const nameLow = file.name.toLowerCase();
    if (!nameLow.endsWith(".dxf")) {
      setStatusMsg("Format invalide — seuls les fichiers .dxf sont acceptés.");
      return;
    }

    setStatusMsg(`Importation de ${file.name}…`);
    setDxfLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/topo/upload-dxf/`,
        { method: "POST", headers: getAuthHeaders(), body: formData }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setStatusMsg(`Erreur : ${err.error || res.statusText}`);
        return;
      }

      const data = await res.json();

      setLayers(data.layers ?? []);
      setActiveLayer(data.layers?.[0]?.name ?? "0");
      renderDXFRef.current?.(data);
      setZoom(cameraRef.current?.zoom ?? 1);
      setHasFile(true);
      setLoadedFileName(file.name);

      const n = data.entities?.length ?? 0;
      setStatusMsg(`${file.name} — ${n} entité${n !== 1 ? "s" : ""} chargée${n !== 1 ? "s" : ""}`);
    } catch (err) {
      setStatusMsg(`Erreur réseau : ${err.message}`);
    } finally {
      setDxfLoading(false);
    }
  };

  const handleFileChange = (e) => {
    importDXF(e.target.files?.[0]);
    e.target.value = "";
  };

  const handleDropZone = (e) => {
    e.preventDefault();
    setDropActive(false);
    importDXF(e.dataTransfer.files?.[0]);
  };

  // =========================================================
  //  EFFECT PRINCIPAL – Three.js (s'exécute UNE SEULE FOIS)
  // =========================================================
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const W = mount.clientWidth;
    const H = mount.clientHeight;

    // ── SCÈNE ──────────────────────────────────────────────
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1e1e1e);
    sceneRef.current = scene;

    // ── CAMERA ORTHOGRAPHIQUE ──────────────────────────────
    const camera = new THREE.OrthographicCamera(W / -2, W / 2, H / 2, H / -2, 1, 1000);
    camera.position.set(0, 0, 500);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // ── RENDERER ───────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // ── CANVAS OVERLAY (snap + rubber-band) ───────────────
    const overlay = overlayRef.current;
    overlay.width  = W;
    overlay.height = H;

    // ── CALQUES ── (vide au démarrage, rempli lors de l'import DXF)
    const layersByName = {};
    layersByNameRef.current = layersByName;

    // ── DRAW ENTITY ────────────────────────────────────────
    function drawEntity(entity, lInfo) {
      if (!lInfo) return null;
      const color = new THREE.Color(lInfo.color);

      switch (entity.type) {
        // LINE
        case "LINE": {
          const mat = new THREE.LineBasicMaterial({ color });
          const geo = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(entity.start[0], entity.start[1], 0),
            new THREE.Vector3(entity.end[0],   entity.end[1],   0),
          ]);
          const line = new THREE.Line(geo, mat);
          line.userData = { type: "LINE", layer: entity.layer };
          return line;
        }
        // CIRCLE
        case "CIRCLE": {
          const mat = new THREE.LineBasicMaterial({ color });
          const pts = [];
          for (let i = 0; i <= 64; i++) {
            const a = (i / 64) * Math.PI * 2;
            pts.push(new THREE.Vector3(
              entity.center[0] + Math.cos(a) * entity.radius,
              entity.center[1] + Math.sin(a) * entity.radius, 0
            ));
          }
          const geo = new THREE.BufferGeometry().setFromPoints(pts);
          const c = new THREE.LineLoop(geo, mat);
          c.userData = { type: "CIRCLE", layer: entity.layer, center: entity.center, radius: entity.radius };
          return c;
        }
        // ARC
        case "ARC": {
          const mat  = new THREE.LineBasicMaterial({ color });
          const curve = new THREE.ArcCurve(
            entity.center[0], entity.center[1], entity.radius,
            entity.startAngle, entity.endAngle
          );
          const pts = curve.getPoints(64).map(p => new THREE.Vector3(p.x, p.y, 0));
          const geo = new THREE.BufferGeometry().setFromPoints(pts);
          const arc = new THREE.Line(geo, mat);
          arc.userData = { type: "ARC", layer: entity.layer, center: entity.center, radius: entity.radius, startAngle: entity.startAngle, endAngle: entity.endAngle };
          return arc;
        }
        // POLYLINE / LWPOLYLINE
        case "POLYLINE":
        case "LWPOLYLINE": {
          const mat  = new THREE.LineBasicMaterial({ color });
          const pts  = entity.points.map(p => new THREE.Vector3(p[0], p[1], 0));
          if (pts.length < 2) return null;
          const geo  = new THREE.BufferGeometry().setFromPoints(pts);
          const poly = entity.closed ? new THREE.LineLoop(geo, mat) : new THREE.Line(geo, mat);
          let area = 0;
          if (entity.closed && pts.length > 2) {
            for (let i = 0; i < pts.length; i++) {
              const p1 = pts[i], p2 = pts[(i + 1) % pts.length];
              area += p1.x * p2.y - p2.x * p1.y;
            }
            area = Math.abs(area / 2);
          }
          poly.userData = { type: "POLYLINE", layer: entity.layer, area };
          return poly;
        }
        // SPLINE
        case "SPLINE": {
          if (!entity.points || entity.points.length < 2) return null;
          const mat   = new THREE.LineBasicMaterial({ color });
          const pts2  = entity.points.map(p => new THREE.Vector2(p[0], p[1]));
          const curve  = new THREE.SplineCurve(pts2);
          const pts3  = curve.getPoints(128).map(p => new THREE.Vector3(p.x, p.y, 0));
          const geo   = new THREE.BufferGeometry().setFromPoints(pts3);
          const spline = new THREE.Line(geo, mat);
          spline.userData = { type: "SPLINE", layer: entity.layer };
          return spline;
        }
        // HATCH
        case "HATCH": {
          const outer = entity.boundary?.outer;
          if (!outer || outer.length < 3) return null;
          const shape = new THREE.Shape();
          shape.moveTo(outer[0][0], outer[0][1]);
          for (let i = 1; i < outer.length; i++) shape.lineTo(outer[i][0], outer[i][1]);
          if (entity.boundary.holes) {
            entity.boundary.holes.forEach(holePts => {
              if (!holePts || holePts.length < 2) return;
              const h = new THREE.Path();
              h.moveTo(holePts[0][0], holePts[0][1]);
              for (let i = 1; i < holePts.length; i++) h.lineTo(holePts[i][0], holePts[i][1]);
              shape.holes.push(h);
            });
          }
          const geo = new THREE.ShapeGeometry(shape);
          const mat = new THREE.MeshBasicMaterial({
            color, side: THREE.DoubleSide, opacity: 0.15, transparent: true,
          });
          const mesh = new THREE.Mesh(geo, mat);
          const outerV2 = outer.map(p => new THREE.Vector2(p[0], p[1]));
          let area = Math.abs(THREE.ShapeUtils.area(outerV2));
          mesh.userData = { type: "HATCH", layer: entity.layer, area };
          return mesh;
        }
        // INSERT / BLOC
        case "INSERT": {
          const grp = new THREE.Group();
          grp.position.set(entity.position[0], entity.position[1], 0);
          grp.rotation.z = THREE.MathUtils.degToRad(entity.rotation || 0);
          grp.scale.set(entity.scale?.[0] || 1, entity.scale?.[1] || 1, entity.scale?.[2] || 1);
          grp.userData = { type: "INSERT", name: entity.name, layer: entity.layer };
          if (entity.block_entities) {
            entity.block_entities.forEach(child => {
              const childLInfo = layersByName[child.layer] || lInfo;
              const childMesh  = drawEntity(child, childLInfo);
              if (childMesh) grp.add(childMesh);
            });
          }
          return grp;
        }
        // TEXT / MTEXT – ignorés (FontLoader async)
        default:
          return null;
      }
    }

    // ── CHARGEMENT DXF (appelé depuis handleFileChange) ───────────────
    renderDXFRef.current = (data) => {
      // Vider les calques existants
      Object.values(layersByName).forEach(l => scene.remove(l.group));
      Object.keys(layersByName).forEach(k => delete layersByName[k]);

      // Créer les nouveaux groupes de calques
      (data.layers ?? []).forEach((l) => {
        const g = new THREE.Group();
        g.name    = l.name;
        g.visible = l.visible;
        scene.add(g);
        layersByName[l.name] = { group: g, color: l.color_hex };
      });
      layersByNameRef.current = layersByName;

      // Dessiner les entités
      (data.entities ?? []).forEach((entity) => {
        const lInfo = layersByName[entity.layer];
        if (!lInfo) return;
        const mesh = drawEntity(entity, lInfo);
        if (mesh) lInfo.group.add(mesh);
      });

      // Remplir le registre de blocs (depuis les INSERT du DXF)
      blockDefsRef.current.clear();
      (data.entities ?? []).forEach(e => {
        if (e.type === "INSERT" && e.block_entities?.length > 0) {
          if (!blockDefsRef.current.has(e.name))
            blockDefsRef.current.set(e.name, { type: "entity_data", data: e.block_entities });
        }
      });
      setBlockNames([...blockDefsRef.current.keys()]);
      dxfDataRef.current = data;   // conservé pour l'export PDF

      // Centrer et zoom-to-fit depuis la bbox retournée par le backend
      const [dcx, dcy] = data.dxf_center ?? [0, 0];
      dxfCenterRef.current = data.dxf_center ?? [0, 0, 0];

      const ext = data.dxf_extent;
      if (ext) {
        const bboxW = Math.abs(ext.max[0] - ext.min[0]);
        const bboxH = Math.abs(ext.max[1] - ext.min[1]);
        if (bboxW > 0 && bboxH > 0) {
          const margin = 0.88; // 12 % de marge autour du dessin
          const fitZoom = Math.min(
            (mount.clientWidth  / bboxW) * margin,
            (mount.clientHeight / bboxH) * margin
          );
          camera.zoom = Math.max(0.0001, Math.min(100000, fitZoom));
        } else {
          camera.zoom = 1;
        }
      } else {
        camera.zoom = 1;
      }

      camera.position.set(dcx, dcy, 500);
      camera.lookAt(dcx, dcy, 0);
      camera.updateProjectionMatrix();
    };

    // ── UTILITAIRES WORLD ↔ SCREEN ─────────────────────────
    function worldToScreen(wx, wy) {
      const v = new THREE.Vector3(wx, wy, 0).project(camera);
      const r = mount.getBoundingClientRect();
      return { x: (v.x * 0.5 + 0.5) * r.width, y: (-v.y * 0.5 + 0.5) * r.height };
    }

    function screenToWorld(clientX, clientY) {
      const r  = mount.getBoundingClientRect();
      const nx = ((clientX - r.left) / r.width)  * 2 - 1;
      const ny = -((clientY - r.top)  / r.height) * 2 + 1;
      const ray = new THREE.Raycaster();
      ray.setFromCamera(new THREE.Vector2(nx, ny), camera);
      const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
      const pt    = new THREE.Vector3();
      ray.ray.intersectPlane(plane, pt);
      return { x: pt.x, y: pt.y };
    }

    // ── DÉTECTION D'ACCROCHAGE ─────────────────────────────
    function findSnap(wx, wy) {
      // rayon d'accrochage en unités monde
      const snapPx    = 14;
      const snapWorld = snapPx / camera.zoom;
      let bestEnd = null, bestMid = null;
      let dEnd = Infinity, dMid = Infinity;

      scene.traverse((obj) => {
        const pos = obj.geometry?.attributes?.position;
        if (!pos) return;
        const n = pos.count;
        for (let i = 0; i < n; i++) {
          const px = pos.getX(i), py = pos.getY(i);
          const d  = Math.hypot(wx - px, wy - py);
          if (d < dEnd && d < snapWorld) { dEnd = d; bestEnd = { type: "endpoint", x: px, y: py }; }
          // Milieu entre deux vertices consécutifs
          if (i < n - 1) {
            const nx2 = pos.getX(i + 1), ny2 = pos.getY(i + 1);
            const mx  = (px + nx2) / 2, my = (py + ny2) / 2;
            const dm  = Math.hypot(wx - mx, wy - my);
            if (dm < dMid && dm < snapWorld) { dMid = dm; bestMid = { type: "midpoint", x: mx, y: my }; }
          }
        }
      });
      return bestEnd || bestMid || null;
    }

    // ── GRILLE ADAPTIVE ───────────────────────────────────
    function drawGrid(ctx) {
      const W = overlay.width, H = overlay.height;

      // Espacement cible ~55px entre lignes en pixels écran
      const targetPx = 55;
      const rawWorld = targetPx / camera.zoom;

      // Arrondir à une valeur « propre » (1,2,5 × 10^n)
      const exp    = Math.floor(Math.log10(rawWorld));
      const base   = Math.pow(10, exp);
      let minor    = base;
      if (rawWorld / base > 5)      minor = base * 10;
      else if (rawWorld / base > 2) minor = base * 5;
      else if (rawWorld / base > 1) minor = base * 2;
      const major = minor * 5;

      // Coins visibles en coordonnées monde
      const tl = screenToWorld(0, 0);
      const br = screenToWorld(W, H);
      if (!tl || !br || !isFinite(tl.x)) return;

      // ── Petites lignes ──
      ctx.save();
      ctx.strokeStyle = "rgba(55,55,65,0.9)";
      ctx.lineWidth   = 0.5;
      const x0m = Math.floor(tl.x / minor) * minor;
      const y0m = Math.floor(br.y  / minor) * minor;
      for (let wx = x0m; wx <= br.x + minor; wx += minor) {
        const sx = worldToScreen(wx, 0).x;
        ctx.beginPath(); ctx.moveTo(sx, 0); ctx.lineTo(sx, H); ctx.stroke();
      }
      for (let wy = y0m; wy <= tl.y + minor; wy += minor) {
        const sy = worldToScreen(0, wy).y;
        ctx.beginPath(); ctx.moveTo(0, sy); ctx.lineTo(W, sy); ctx.stroke();
      }

      // ── Grandes lignes ──
      ctx.strokeStyle = "rgba(80,80,95,1)";
      ctx.lineWidth   = 1;
      const x0M = Math.floor(tl.x / major) * major;
      const y0M = Math.floor(br.y  / major) * major;
      for (let wx = x0M; wx <= br.x + major; wx += major) {
        const sx = worldToScreen(wx, 0).x;
        ctx.beginPath(); ctx.moveTo(sx, 0); ctx.lineTo(sx, H); ctx.stroke();
      }
      for (let wy = y0M; wy <= tl.y + major; wy += major) {
        const sy = worldToScreen(0, wy).y;
        ctx.beginPath(); ctx.moveTo(0, sy); ctx.lineTo(W, sy); ctx.stroke();
      }

      // ── Axes X/Y (0,0) ──
      const ox = worldToScreen(0, 0);
      ctx.strokeStyle = "rgba(120,120,140,0.6)";
      ctx.lineWidth   = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(ox.x, 0);   ctx.lineTo(ox.x, H); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, ox.y);   ctx.lineTo(W, ox.y); ctx.stroke();
      ctx.setLineDash([]);

      // Étiquettes des grandes lignes (si assez de place)
      if (major * camera.zoom > 60) {
        ctx.fillStyle = "rgba(100,100,120,0.8)";
        ctx.font = "9px monospace";
        for (let wx = x0M; wx <= br.x + major; wx += major) {
          const sx = worldToScreen(wx, 0).x;
          ctx.fillText(wx.toFixed(wx % 1 === 0 ? 0 : 2), sx + 3, H - 4);
        }
        for (let wy = y0M; wy <= tl.y + major; wy += major) {
          const sy = worldToScreen(0, wy).y;
          ctx.fillText(wy.toFixed(wy % 1 === 0 ? 0 : 2), 4, sy - 3);
        }
      }
      ctx.restore();
    }

    // ── RENDU OVERLAY COMPLET ─────────────────────────────
    function redrawOverlay() {
      const ctx = overlay.getContext("2d");
      ctx.clearRect(0, 0, overlay.width, overlay.height);

      // 1. Grille
      drawGrid(ctx);

      const snap    = snapPtRef.current;
      const preview = previewPtRef.current;
      const pts     = drawingPtsRef.current;
      const drawing = isDrawingRef.current;
      const tool    = selectedToolRef.current;

      // 2. Hachures (rendu canvas avec clipping)
      drawHatches(ctx);

      // 2b. Cotations
      drawDimensions(ctx);
      drawDimPreview(ctx);

      // 3. Textes utilisateur (rendu en world-space)
      textObjectsRef.current.forEach(({ text, x, y, color, size }) => {
        const sp = worldToScreen(x, y);
        const screenSize = Math.max(8, size * camera.zoom);
        if (screenSize < 3) return;
        ctx.save();
        ctx.font      = `${screenSize}px Consolas, monospace`;
        ctx.fillStyle = color || "#00aaff";
        ctx.fillText(text, sp.x, sp.y);
        ctx.restore();
      });

      // 3. Rubber-band LIGNE / POLYLIGNE
      if (drawing && (tool === "line" || tool === "polyline")) {
        // Segments déjà posés
        if (pts.length > 1) {
          ctx.save();
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth   = 1;
          ctx.setLineDash([]);
          ctx.beginPath();
          const f = worldToScreen(pts[0].x, pts[0].y);
          ctx.moveTo(f.x, f.y);
          for (let i = 1; i < pts.length; i++) {
            const p = worldToScreen(pts[i].x, pts[i].y);
            ctx.lineTo(p.x, p.y);
          }
          ctx.stroke();
          ctx.restore();
        }
        // Rubber-band vers curseur
        if (pts.length > 0 && preview) {
          const start = worldToScreen(pts[pts.length - 1].x, pts[pts.length - 1].y);
          const end   = worldToScreen(preview.x, preview.y);
          ctx.save();
          ctx.strokeStyle = "#00d4ff";
          ctx.lineWidth   = 1;
          ctx.setLineDash([5, 4]);
          ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.lineTo(end.x, end.y); ctx.stroke();
          ctx.restore();
        }
        // Marqueurs points
        pts.forEach((pt) => {
          const sp = worldToScreen(pt.x, pt.y);
          ctx.fillStyle = "#00d4ff";
          ctx.fillRect(sp.x - 2.5, sp.y - 2.5, 5, 5);
        });
      }

      // 4. Rubber-band CERCLE
      if (drawing && tool === "circle" && pts.length === 1 && preview) {
        const center  = pts[0];
        const cs      = worldToScreen(center.x, center.y);
        const ps      = worldToScreen(preview.x, preview.y);
        const radiusPx = Math.hypot(ps.x - cs.x, ps.y - cs.y);
        // Cercle preview
        ctx.save();
        ctx.strokeStyle = "#00d4ff";
        ctx.lineWidth   = 1;
        ctx.setLineDash([5, 4]);
        ctx.beginPath();
        ctx.arc(cs.x, cs.y, radiusPx, 0, Math.PI * 2);
        ctx.stroke();
        // Ligne rayon
        ctx.setLineDash([3, 3]);
        ctx.strokeStyle = "rgba(0,212,255,0.5)";
        ctx.beginPath(); ctx.moveTo(cs.x, cs.y); ctx.lineTo(ps.x, ps.y); ctx.stroke();
        // Centre
        ctx.setLineDash([]);
        ctx.fillStyle = "#00d4ff";
        ctx.fillRect(cs.x - 3, cs.y - 3, 6, 6);
        // Rayon en label
        const worldRadius = Math.hypot(preview.x - center.x, preview.y - center.y);
        ctx.fillStyle = "#00d4ff";
        ctx.font = "11px monospace";
        ctx.fillText(`r = ${worldRadius.toFixed(3)}`, ps.x + 10, ps.y);
        ctx.restore();
      }

      // 5. Marqueur centre cercle (phase 1 posé)
      if (drawing && tool === "circle" && pts.length === 1) {
        const cs = worldToScreen(pts[0].x, pts[0].y);
        ctx.fillStyle = "#00d4ff";
        ctx.fillRect(cs.x - 3, cs.y - 3, 6, 6);
      }

      // 5b. Rubber-band RECTANGLE
      if (drawing && tool === "rectangle" && pts.length === 1 && preview) {
        const p1 = worldToScreen(pts[0].x, pts[0].y);
        const p2 = worldToScreen(preview.x, preview.y);
        const rx = Math.min(p1.x, p2.x), ry = Math.min(p1.y, p2.y);
        const rw = Math.abs(p2.x - p1.x), rh = Math.abs(p2.y - p1.y);
        ctx.save();
        ctx.strokeStyle = "#00d4ff";
        ctx.lineWidth   = 1;
        ctx.setLineDash([5, 4]);
        ctx.strokeRect(rx, ry, rw, rh);
        ctx.fillStyle   = "rgba(0,212,255,0.04)";
        ctx.fillRect(rx, ry, rw, rh);
        ctx.setLineDash([]);
        const dx = Math.abs(preview.x - pts[0].x), dy = Math.abs(preview.y - pts[0].y);
        ctx.fillStyle = "#00d4ff";
        ctx.font = "11px monospace";
        ctx.fillText(`${dx.toFixed(3)} × ${dy.toFixed(3)}`, Math.max(p1.x, p2.x) + 6, Math.min(p1.y, p2.y) - 5);
        ctx.fillRect(p1.x - 3, p1.y - 3, 6, 6);
        ctx.restore();
      }

      // 6. Indicateur d'accrochage
      if (snap) {
        const sp = worldToScreen(snap.x, snap.y);
        ctx.save();
        ctx.strokeStyle = "#ffff00";
        ctx.fillStyle   = "rgba(255,255,0,0.12)";
        ctx.lineWidth   = 2;
        ctx.shadowColor = "#ffff00";
        ctx.shadowBlur  = 6;

        if (snap.type === "endpoint") {
          ctx.strokeRect(sp.x - 7, sp.y - 7, 14, 14);
          ctx.fillRect(sp.x - 7, sp.y - 7, 14, 14);
        } else if (snap.type === "midpoint") {
          ctx.beginPath();
          ctx.moveTo(sp.x, sp.y - 9); ctx.lineTo(sp.x + 8, sp.y + 6); ctx.lineTo(sp.x - 8, sp.y + 6);
          ctx.closePath(); ctx.stroke(); ctx.fill();
        } else if (snap.type === "intersection") {
          ctx.beginPath();
          ctx.moveTo(sp.x - 8, sp.y - 8); ctx.lineTo(sp.x + 8, sp.y + 8);
          ctx.moveTo(sp.x + 8, sp.y - 8); ctx.lineTo(sp.x - 8, sp.y + 8);
          ctx.stroke();
        } else if (snap.type === "nearest") {
          ctx.beginPath(); ctx.arc(sp.x, sp.y, 7, 0, Math.PI * 2); ctx.stroke();
        }

        ctx.shadowBlur = 0;
        ctx.fillStyle  = "#ffff00";
        ctx.font       = "10px monospace";
        const labels   = { endpoint: "Point final", midpoint: "Milieu", intersection: "Intersection", nearest: "Le plus proche" };
        ctx.fillText(labels[snap.type] || snap.type, sp.x + 12, sp.y - 3);
        ctx.restore();
      }
    }

    // Expose redrawOverlay pour les useEffect extérieurs (ex: dimStyle change)
    redrawOverlayRef.current = redrawOverlay;

    // ══════════════════════════════════════════════════════
    //  BLOCS – gestion AutoCAD-like
    // ══════════════════════════════════════════════════════

    let ghostInsert = null; // THREE.Group fantôme (mode insertion)

    // ── Trouver l'ancêtre INSERT d'un objet THREE.js ──────
    function findInsertAncestor(obj) {
      let cur = obj?.parent;
      while (cur && cur !== scene) {
        if (cur.userData?.type === "INSERT") return cur;
        cur = cur.parent;
      }
      return null;
    }

    // ── Créer le fantôme d'insertion ──────────────────────
    function createBlockGhost(name) {
      if (ghostInsert) { scene.remove(ghostInsert); ghostInsert = null; }
      const def = blockDefsRef.current.get(name);
      if (!def) return;
      const ghostLInfo = { color: "#4a9eff" };
      const grp = new THREE.Group();
      grp.userData = { type: "_GHOST_INSERT" };
      if (def.type === "entity_data") {
        def.data.forEach(child => {
          const m = drawEntity(child, ghostLInfo);
          if (m) {
            m.traverse(o => {
              if (o.material) {
                o.material = o.material.clone();
                o.material.transparent = true;
                o.material.opacity = 0.45;
              }
            });
            grp.add(m);
          }
        });
      } else if (def.type === "three_clone") {
        const clone = def.data.clone(true);
        clone.traverse(o => {
          if (o.material) {
            o.material = o.material.clone();
            o.material.transparent = true;
            o.material.opacity = 0.45;
          }
        });
        grp.add(clone);
      }
      scene.add(grp);
      ghostInsert = grp;
    }
    createBlockGhostRef.current = createBlockGhost;

    // ── Supprimer le fantôme ─────────────────────────────
    function removeBlockGhost() {
      if (ghostInsert) { scene.remove(ghostInsert); ghostInsert = null; }
    }
    removeBlockGhostRef.current = removeBlockGhost;

    // ── Placer un INSERT permanent ────────────────────────
    function commitInsertBlock(wx, wy) {
      const name = insertingBlockRef.current;
      if (!name) return;
      const def = blockDefsRef.current.get(name);
      if (!def) return;
      const layerName = activeLayerRef.current;
      const lInfo     = layersByNameRef.current[layerName];
      if (!lInfo) return;

      const grp = new THREE.Group();
      grp.position.set(wx, wy, 0);
      grp.userData = { type: "INSERT", name, layer: layerName, isUserDrawn: true };

      if (def.type === "entity_data") {
        def.data.forEach(child => {
          const m = drawEntity(child, lInfo);
          if (m) grp.add(m);
        });
      } else if (def.type === "three_clone") {
        const clone = def.data.clone(true);
        clone.traverse(o => {
          if (o.material) { o.material = o.material.clone(); o.material.transparent = false; o.material.opacity = 1; }
        });
        grp.add(clone);
      }
      lInfo.group.add(grp);
      // AutoCAD : reste en mode insertion jusqu'à ESC
      redrawOverlay();
    }

    // ── Exploser un INSERT ────────────────────────────────
    function explodeInsert(insertGrp) {
      if (!insertGrp || insertGrp.userData?.type !== "INSERT") return;
      const parent = insertGrp.parent;
      if (!parent) return;
      const children = [...insertGrp.children];
      insertGrp.updateWorldMatrix(true, false);
      children.forEach(child => {
        child.applyMatrix4(insertGrp.matrixWorld);
        parent.add(child);
      });
      parent.remove(insertGrp);
      selectedObjsRef.current = [];
      setSelectedObject(null);
      setSelectedEntities([]);
      setStatusMsg("Bloc explosé");
      redrawOverlay();
    }
    explodeInsertRef.current = explodeInsert;

    // ── Créer un bloc depuis la sélection ─────────────────
    function createBlockFromSelection(name) {
      name = name.trim();
      const objs = selectedObjsRef.current;
      if (!objs.length || !name) return;

      // Centroïde
      let cx = 0, cy = 0;
      objs.forEach(obj => {
        const wp = new THREE.Vector3();
        obj.getWorldPosition(wp);
        cx += wp.x; cy += wp.y;
      });
      cx /= objs.length; cy /= objs.length;

      // Wrapper THREE.Group (template de clone)
      const template = new THREE.Group();
      objs.forEach(obj => {
        const clone = obj.clone(true);
        const wp = new THREE.Vector3();
        obj.getWorldPosition(wp);
        clone.position.set(wp.x - cx, wp.y - cy, 0);
        template.add(clone);
        // Restaurer couleur de l'original
        if (obj.material && obj.userData._origColorSel !== undefined)
          obj.material.color.setHex(obj.userData._origColorSel);
      });

      blockDefsRef.current.set(name, { type: "three_clone", data: template });
      setBlockNames([...blockDefsRef.current.keys()]);

      // Remplacer la sélection par un INSERT group au centroïde
      const layerName = activeLayerRef.current;
      const lInfo     = layersByNameRef.current[layerName];
      if (lInfo) {
        const insertGrp = new THREE.Group();
        insertGrp.position.set(cx, cy, 0);
        insertGrp.userData = { type: "INSERT", name, layer: layerName, isUserDrawn: true };
        objs.forEach(obj => {
          const parent = obj.parent;
          if (parent) parent.remove(obj);
          const wp = new THREE.Vector3();
          obj.getWorldPosition(wp);
          obj.position.set(wp.x - cx, wp.y - cy, 0);
          insertGrp.add(obj);
        });
        lInfo.group.add(insertGrp);
      }
      selectedObjsRef.current = [];
      setSelectedObject(null);
      setSelectedEntities([]);
      setStatusMsg(`Bloc "${name}" créé`);
      redrawOverlay();
    }
    createBlockFromSelRef.current = createBlockFromSelection;

    // ── RÉINITIALISER L'ÉTAT DE DESSIN ────────────────────
    function resetDrawState() {
      drawingPtsRef.current = [];
      isDrawingRef.current  = false;
      previewPtRef.current  = null;
      snapPtRef.current     = null;
      setSnapLabel("");
    }

    // ── VALIDER LIGNE / POLYLIGNE ──────────────────────────
    function commitDrawing() {
      const pts       = drawingPtsRef.current;
      const tool      = selectedToolRef.current;
      const layerName = activeLayerRef.current;
      const lInfo     = layersByNameRef.current[layerName];
      if (!lInfo || pts.length < 2) return;

      const mat  = new THREE.LineBasicMaterial({ color: new THREE.Color("#00aaff") });
      const vec3 = pts.map(p => new THREE.Vector3(p.x, p.y, 0));
      const geo  = new THREE.BufferGeometry().setFromPoints(vec3);
      const obj  = new THREE.Line(geo, mat);
      obj.userData = { type: tool === "line" ? "LINE" : "POLYLINE", layer: layerName, isUserDrawn: true };
      lInfo.group.add(obj);

      resetDrawState();
      // ← retour automatique en sélection après dessin
      setSelectedTool("select");
      redrawOverlay();
    }

    // ── VALIDER CERCLE ─────────────────────────────────────
    function commitCircle() {
      const pts       = drawingPtsRef.current;
      const layerName = activeLayerRef.current;
      const lInfo     = layersByNameRef.current[layerName];
      if (!lInfo || pts.length < 2) return;

      const [center, edge] = pts;
      const radius = Math.hypot(edge.x - center.x, edge.y - center.y);
      if (radius < 1e-9) return;

      const circlePts = [];
      for (let i = 0; i <= 64; i++) {
        const a = (i / 64) * Math.PI * 2;
        circlePts.push(new THREE.Vector3(
          center.x + Math.cos(a) * radius,
          center.y + Math.sin(a) * radius, 0
        ));
      }
      const mat = new THREE.LineBasicMaterial({ color: new THREE.Color("#00aaff") });
      const geo = new THREE.BufferGeometry().setFromPoints(circlePts);
      const obj = new THREE.LineLoop(geo, mat);
      obj.userData = { type: "CIRCLE", layer: layerName, center: [center.x, center.y], radius, isUserDrawn: true };
      lInfo.group.add(obj);

      resetDrawState();
      setSelectedTool("select");
      redrawOverlay();
    }

    function cancelDrawing() {
      resetDrawState();
      redrawOverlay();
    }

    // ── POINT DANS POLYGONE ────────────────────────────────
    function pointInPolygon(px, py, polygon) {
      let inside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x, yi = polygon[i].y;
        const xj = polygon[j].x, yj = polygon[j].y;
        const cross = ((yi > py) !== (yj > py)) &&
          (px < (xj - xi) * (py - yi) / (yj - yi) + xi);
        if (cross) inside = !inside;
      }
      return inside;
    }

    // ── POLYGONES FERMÉS EXPLICITES (LineLoop / Line fermée / Mesh) ──
    function getClosedPolygons() {
      const result  = [];
      const closedTol = 1e-4;

      function pushPolygon(pts) {
        if (pts.length < 3) return;
        let area = 0;
        for (let i = 0; i < pts.length; i++) {
          const p1 = pts[i], p2 = pts[(i + 1) % pts.length];
          area += p1.x * p2.y - p2.x * p1.y;
        }
        if (Math.abs(area) < 1e-10) return;
        result.push({ polygon: pts, area: Math.abs(area / 2) });
      }

      scene.traverse((obj) => {
        if (obj instanceof THREE.LineLoop) {
          const pos = obj.geometry?.attributes?.position;
          if (!pos || pos.count < 3) return;
          const pts = [];
          for (let i = 0; i < pos.count; i++)
            pts.push({ x: pos.getX(i), y: pos.getY(i) });
          pushPolygon(pts);
          return;
        }
        if (obj instanceof THREE.Line) {
          const pos = obj.geometry?.attributes?.position;
          if (!pos || pos.count < 4) return;
          const n = pos.count;
          if (Math.hypot(pos.getX(n-1)-pos.getX(0), pos.getY(n-1)-pos.getY(0)) > closedTol) return;
          const pts = [];
          for (let i = 0; i < n - 1; i++)
            pts.push({ x: pos.getX(i), y: pos.getY(i) });
          pushPolygon(pts);
          return;
        }
        if (obj instanceof THREE.Mesh) {
          const pos = obj.geometry?.attributes?.position;
          if (!pos || pos.count < 3) return;
          const seen = new Map();
          for (let i = 0; i < pos.count; i++) {
            const x = +pos.getX(i).toFixed(6), y = +pos.getY(i).toFixed(6);
            seen.set(`${x},${y}`, { x, y });
          }
          const uniq = [...seen.values()];
          if (uniq.length < 3) return;
          const cx = uniq.reduce((s,p) => s+p.x, 0) / uniq.length;
          const cy = uniq.reduce((s,p) => s+p.y, 0) / uniq.length;
          uniq.sort((a,b) => Math.atan2(a.y-cy, a.x-cx) - Math.atan2(b.y-cy, b.x-cx));
          pushPolygon(uniq);
        }
      });
      return result;
    }

    // ── GRAPHE DE SEGMENTS (pour polyligne décomposée) ───────
    function buildSegmentGraph(snapTol) {
      const T    = snapTol || 0.05;
      const nodes = [];          // [{x, y, edges:[nodeIdx]}]
      const grid  = new Map();   // cellKey → nodeIdx

      const cellKey = (x, y) =>
        `${Math.round(x / T)},${Math.round(y / T)}`;

      function findNear(x, y) {
        const cx = Math.round(x/T), cy = Math.round(y/T);
        for (let dx = -1; dx <= 1; dx++)
          for (let dy = -1; dy <= 1; dy++) {
            const k = `${cx+dx},${cy+dy}`;
            if (!grid.has(k)) continue;
            const idx = grid.get(k);
            if (Math.hypot(nodes[idx].x - x, nodes[idx].y - y) <= T) return idx;
          }
        return -1;
      }

      function getNode(x, y) {
        const e = findNear(x, y);
        if (e >= 0) return e;
        const idx = nodes.length;
        nodes.push({ x, y, edges: [] });
        grid.set(cellKey(x, y), idx);
        return idx;
      }

      function addEdge(ai, bi) {
        if (ai === bi) return;
        if (!nodes[ai].edges.includes(bi)) nodes[ai].edges.push(bi);
        if (!nodes[bi].edges.includes(ai)) nodes[bi].edges.push(ai);
      }

      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) return;
        const pos = obj.geometry?.attributes?.position;
        if (!pos || pos.count < 2) return;
        const n     = pos.count;
        const isLoop = obj instanceof THREE.LineLoop;
        for (let i = 0; i < n - 1; i++)
          addEdge(getNode(pos.getX(i), pos.getY(i)),
                  getNode(pos.getX(i+1), pos.getY(i+1)));
        if (isLoop)
          addEdge(getNode(pos.getX(n-1), pos.getY(n-1)),
                  getNode(pos.getX(0),   pos.getY(0)));
      });
      return nodes;
    }

    // ── TRACÉ D'UNE FACE PLANAIRE (algorithme CCW minimal) ──
    function traceFace(nodes, startA, startB) {
      const MAX = 3000;
      const poly = [];
      let curA = startA, curB = startB;

      for (let iter = 0; iter < MAX; iter++) {
        poly.push(curA);
        const nA = nodes[curA], nB = nodes[curB];
        // Direction inverse : de curB vers curA
        const dirBA = Math.atan2(nA.y - nB.y, nA.x - nB.x);

        let bestDiff = Infinity, nextNode = -1;
        for (const nb of nB.edges) {
          const angle = Math.atan2(nodes[nb].y - nB.y, nodes[nb].x - nB.x);
          let diff = angle - dirBA;
          while (diff <= 0) diff += 2 * Math.PI;
          if (diff < bestDiff) { bestDiff = diff; nextNode = nb; }
        }
        if (nextNode === -1) return null;

        curA = curB;
        curB = nextNode;
        if (curA === startA && curB === startB) break;
      }

      if (poly.length < 3) return null;
      return poly.map(i => ({ x: nodes[i].x, y: nodes[i].y }));
    }

    // ── DÉTECTION FRONTIÈRE AU POINT (wx, wy) ───────────────
    function findBoundaryPolygon(wx, wy, nodes) {
      if (!nodes || nodes.length < 3) return null;

      // Rayon vertical +Y : trouver le segment le plus bas au-dessus du clic
      let nearestY = Infinity;
      let startA = -1, startB = -1;

      for (let i = 0; i < nodes.length; i++) {
        for (const j of nodes[i].edges) {
          if (j <= i) continue;                          // arête non-dirigée : une seule fois
          const ax = nodes[i].x, ay = nodes[i].y;
          const bx = nodes[j].x, by = nodes[j].y;
          const dx = bx - ax;
          if (Math.abs(dx) < 1e-9) continue;            // segment vertical → ignore

          const t = (wx - ax) / dx;
          if (t < -1e-6 || t > 1 + 1e-6) continue;     // wx hors du segment
          const iy = ay + t * (by - ay);
          if (iy <= wy + 1e-9) continue;                // en-dessous ou au niveau du clic

          if (iy < nearestY) {
            nearestY = iy;
            // dx>0 (gauche→droite) : face en-dessous = droite de A→B = gauche de B→A
            if (dx > 0) { startA = j; startB = i; }
            else        { startA = i; startB = j; }
          }
        }
      }
      if (startA === -1) return null;

      const poly = traceFace(nodes, startA, startB);
      if (!poly) return null;
      if (!pointInPolygon(wx, wy, poly)) return null;   // vérification finale
      return poly;
    }

    // ── ÎLOTS : polygones entièrement à l'intérieur d'un autre ─
    function findIslands(outerPolygon, allPolygons) {
      // Signature de l'extérieur pour l'exclure
      const outerKey = outerPolygon
        .map(p => `${p.x.toFixed(4)},${p.y.toFixed(4)}`).join("|");

      return allPolygons
        .filter(({ polygon }) => {
          const k = polygon.map(p => `${p.x.toFixed(4)},${p.y.toFixed(4)}`).join("|");
          if (k === outerKey) return false;
          // Tous les points de l'îlot doivent être à l'intérieur de l'extérieur
          return polygon.every(pt => pointInPolygon(pt.x, pt.y, outerPolygon));
        })
        .map(({ polygon }) => polygon);
    }

    // ── VALIDER RECTANGLE ──────────────────────────────────
    function commitRectangle() {
      const pts       = drawingPtsRef.current;
      const layerName = activeLayerRef.current;
      const lInfo     = layersByNameRef.current[layerName];
      if (!lInfo || pts.length < 2) return;

      const [p1, p2] = pts;
      const corners  = [
        new THREE.Vector3(p1.x, p1.y, 0),
        new THREE.Vector3(p2.x, p1.y, 0),
        new THREE.Vector3(p2.x, p2.y, 0),
        new THREE.Vector3(p1.x, p2.y, 0),
        new THREE.Vector3(p1.x, p1.y, 0), // fermeture
      ];
      const mat = new THREE.LineBasicMaterial({ color: new THREE.Color("#00aaff") });
      const geo = new THREE.BufferGeometry().setFromPoints(corners);
      const obj = new THREE.Line(geo, mat);
      obj.userData = { type: "RECTANGLE", layer: layerName, isUserDrawn: true };
      lInfo.group.add(obj);

      resetDrawState();
      setSelectedTool("select");
      redrawOverlay();
    }

    // ── VALIDER HACHURE ────────────────────────────────────
    function commitHatch(wx, wy) {
      const hs      = hatchStyleRef.current ?? {};
      const snapTol = hs.snapTol ?? 0.05;

      // 1. Essai via graphe de segments (polyligne décomposée + zones quelconques)
      let polygon = null;
      try {
        const nodes = buildSegmentGraph(snapTol);
        polygon = findBoundaryPolygon(wx, wy, nodes);
      } catch (_) { polygon = null; }

      // 2. Fallback : polyligne fermée explicite la plus petite
      if (!polygon) {
        const candidates = getClosedPolygons().filter(({ polygon: p }) =>
          pointInPolygon(wx, wy, p)
        );
        if (candidates.length === 0) {
          setStatusMsg("HACHURE – Aucune zone fermée trouvée. Cliquer à l'intérieur d'un contour fermé.");
          return;
        }
        candidates.sort((a, b) => a.area - b.area);
        polygon = candidates[0].polygon;
      }

      // 3. Détection des îlots (trous intérieurs)
      const allClosed = getClosedPolygons();
      const islands   = findIslands(polygon, allClosed);

      const layerName = activeLayerRef.current;
      const lInfo     = layersByNameRef.current[layerName];
      const color     = (hs.useLayerColor !== false) ? (lInfo?.color || "#00aaff") : (hs.color || "#00aaff");

      hatchObjectsRef.current = [
        ...hatchObjectsRef.current,
        {
          boundary: polygon,
          islands,
          angle:    hs.angle   ?? 45,
          spacing:  hs.spacing ?? 0.3,
          opacity:  hs.opacity ?? 0.75,
          pattern:  hs.pattern ?? "lines",
          color,
        },
      ];
      hatchPreviewRef.current = null;
      resetDrawState();
      setSelectedTool("select");
      redrawOverlay();
    }

    // ── HELPER : tracer des lignes parallèles dans le clip ──
    function fillHatchLines(ctx, mnX, mxX, mnY, mxY, angleDeg, spacingPx) {
      const rad  = (angleDeg * Math.PI) / 180;
      const cos  = Math.cos(rad), sin = Math.sin(rad);
      const diag = Math.hypot(mxX - mnX, mxY - mnY) + spacingPx * 2;
      const cx   = (mnX + mxX) / 2, cy = (mnY + mxY) / 2;
      const steps = Math.ceil(diag / spacingPx) + 1;
      for (let i = -steps; i <= steps; i++) {
        const off = i * spacingPx;
        const px = cx + off * (-sin), py = cy + off * cos;
        ctx.beginPath();
        ctx.moveTo(px - cos * diag, py - sin * diag);
        ctx.lineTo(px + cos * diag, py + sin * diag);
        ctx.stroke();
      }
    }

    // ── DESSINER LES HACHURES (canvas overlay) ────────────
    function drawHatches(ctx) {
      // Hachures validées
      hatchObjectsRef.current.forEach(({ boundary, islands, angle, spacing, color, pattern, opacity }) => {
        const screenPts = boundary.map(p => worldToScreen(p.x, p.y));
        if (screenPts.length < 3) return;

        // Clip au contour (even-odd pour créer des trous avec les îlots intérieurs)
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(screenPts[0].x, screenPts[0].y);
        for (let i = 1; i < screenPts.length; i++) ctx.lineTo(screenPts[i].x, screenPts[i].y);
        ctx.closePath();
        if (islands && islands.length > 0) {
          for (const island of islands) {
            const ipts = island.map(p => worldToScreen(p.x, p.y));
            if (ipts.length < 3) continue;
            ctx.moveTo(ipts[0].x, ipts[0].y);
            for (let i = 1; i < ipts.length; i++) ctx.lineTo(ipts[i].x, ipts[i].y);
            ctx.closePath();
          }
          ctx.clip("evenodd");
        } else {
          ctx.clip();
        }

        // Boîte englobante
        const xs  = screenPts.map(p => p.x);
        const ys  = screenPts.map(p => p.y);
        const mnX = Math.min(...xs) - 2, mxX = Math.max(...xs) + 2;
        const mnY = Math.min(...ys) - 2, mxY = Math.max(...ys) + 2;
        const spacingPx = Math.max(3, spacing * camera.zoom);

        ctx.strokeStyle = color;
        ctx.fillStyle   = color;
        ctx.lineWidth   = 0.8;
        ctx.globalAlpha = opacity ?? 0.75;

        switch (pattern) {
          case "cross":
            fillHatchLines(ctx, mnX, mxX, mnY, mxY, angle,       spacingPx);
            fillHatchLines(ctx, mnX, mxX, mnY, mxY, angle + 90,  spacingPx);
            break;
          case "square":
            fillHatchLines(ctx, mnX, mxX, mnY, mxY, 0,   spacingPx);
            fillHatchLines(ctx, mnX, mxX, mnY, mxY, 90,  spacingPx);
            break;
          case "diamond":
            fillHatchLines(ctx, mnX, mxX, mnY, mxY, 45,  spacingPx);
            fillHatchLines(ctx, mnX, mxX, mnY, mxY, 135, spacingPx);
            break;
          case "dot": {
            const sp = spacingPx;
            for (let x = mnX; x <= mxX; x += sp) {
              for (let y = mnY; y <= mxY; y += sp) {
                ctx.beginPath();
                ctx.arc(x, y, Math.max(1, sp * 0.12), 0, Math.PI * 2);
                ctx.fill();
              }
            }
            break;
          }
          case "zigzag": {
            const rad  = (angle * Math.PI) / 180;
            const cos  = Math.cos(rad), sin = Math.sin(rad);
            const diag = Math.hypot(mxX - mnX, mxY - mnY) + spacingPx * 2;
            const cx   = (mnX + mxX) / 2, cy = (mnY + mxY) / 2;
            const steps = Math.ceil(diag / spacingPx) + 1;
            const zamp  = spacingPx * 0.35; // amplitude zigzag
            for (let i = -steps; i <= steps; i++) {
              const off = i * spacingPx;
              const px0 = cx + off * (-sin), py0 = cy + off * cos;
              ctx.beginPath();
              for (let t = -diag; t <= diag; t += spacingPx * 0.5) {
                const flip = (Math.floor(t / (spacingPx * 0.5)) % 2 === 0) ? 1 : -1;
                const tx = px0 + cos * t + flip * (-sin) * zamp;
                const ty = py0 + sin * t + flip * cos    * zamp;
                if (t === -diag) ctx.moveTo(tx, ty); else ctx.lineTo(tx, ty);
              }
              ctx.stroke();
            }
            break;
          }
          case "honeycomb": {
            // Hexagones
            const sp = spacingPx * 1.8;
            const h  = sp * Math.sqrt(3) / 2;
            for (let row = -2; row * h < mxY - mnY + h * 3; row++) {
              for (let col = -2; col * sp * 1.5 < mxX - mnX + sp * 3; col++) {
                const hx = mnX + col * sp * 1.5 + (row % 2 === 0 ? 0 : sp * 0.75);
                const hy = mnY + row * h;
                ctx.beginPath();
                for (let k = 0; k < 6; k++) {
                  const a = (k * 60 - 30) * Math.PI / 180;
                  const px2 = hx + sp * 0.5 * Math.cos(a);
                  const py2 = hy + sp * 0.5 * Math.sin(a);
                  k === 0 ? ctx.moveTo(px2, py2) : ctx.lineTo(px2, py2);
                }
                ctx.closePath();
                ctx.stroke();
              }
            }
            break;
          }
          case "brick": {
            // Briques horizontales décalées
            const bh = spacingPx * 0.5, bw = spacingPx * 1.2;
            for (let row = -2; row * bh < mxY - mnY + bh * 3; row++) {
              const y0 = mnY + row * bh;
              const offset = (row % 2 === 0) ? 0 : bw * 0.5;
              // Ligne horizontale
              ctx.beginPath(); ctx.moveTo(mnX, y0); ctx.lineTo(mxX, y0); ctx.stroke();
              // Lignes verticales décalées
              for (let x0 = mnX - bw + offset; x0 <= mxX + bw; x0 += bw) {
                ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x0, y0 + bh); ctx.stroke();
              }
            }
            break;
          }
          default: // "lines"
            fillHatchLines(ctx, mnX, mxX, mnY, mxY, angle, spacingPx);
        }

        ctx.globalAlpha = 1;
        ctx.restore();

        // Contour du polygone
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(screenPts[0].x, screenPts[0].y);
        for (let i = 1; i < screenPts.length; i++) ctx.lineTo(screenPts[i].x, screenPts[i].y);
        ctx.closePath();
        ctx.strokeStyle = color;
        ctx.lineWidth   = 0.6;
        ctx.globalAlpha = 0.5;
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.restore();
      });

      // Preview de la zone survolée (mode hachure)
      const prev = hatchPreviewRef.current;
      if (prev && selectedToolRef.current === "hatch") {
        const screenPts = prev.map(p => worldToScreen(p.x, p.y));
        if (screenPts.length >= 3) {
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(screenPts[0].x, screenPts[0].y);
          for (let i = 1; i < screenPts.length; i++) ctx.lineTo(screenPts[i].x, screenPts[i].y);
          ctx.closePath();
          ctx.strokeStyle = "#00d4ff";
          ctx.lineWidth   = 1.5;
          ctx.setLineDash([6, 3]);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = "rgba(0,212,255,0.07)";
          ctx.fill();
          ctx.restore();
        }
      }
    }

    // ══════════════════════════════════════════════════════
    //  COTATION – fonctions internes (accès à camera, scene, worldToScreen)
    // ══════════════════════════════════════════════════════

    // ── Détection arc/cercle le plus proche ───────────────
    function detectNearCircleArc(wx, wy) {
      const snapW = 20 / camera.zoom;
      let best = null, bestDist = Infinity;
      scene.traverse((obj) => {
        const ud = obj.userData;
        if ((ud.type === "CIRCLE" || ud.type === "ARC") && ud.center && ud.radius != null) {
          const d = Math.abs(Math.hypot(wx - ud.center[0], wy - ud.center[1]) - ud.radius);
          if (d < snapW && d < bestDist) {
            bestDist = d;
            best = { type: ud.type, cx: ud.center[0], cy: ud.center[1], radius: ud.radius,
                     startAngle: ud.startAngle, endAngle: ud.endAngle };
          }
        }
      });
      return best;
    }

    // ── Flèche remplie ────────────────────────────────────
    function drawDimArrow(ctx, spx, spy, ddx, ddy, sizePx) {
      const hw = sizePx / 3;
      ctx.beginPath();
      ctx.moveTo(spx, spy);
      ctx.lineTo(spx - ddx * sizePx + ddy * hw, spy - ddy * sizePx - ddx * hw);
      ctx.lineTo(spx - ddx * sizePx - ddy * hw, spy - ddy * sizePx + ddx * hw);
      ctx.closePath();
      ctx.fill();
    }

    // ── Étiquette avec fond ───────────────────────────────
    function drawDimLabel(ctx, str, sx, sy, color, fontPx) {
      const pad = 3;
      ctx.font = `${fontPx}px monospace`;
      const w = ctx.measureText(str).width + pad * 2;
      const h = fontPx + pad;
      ctx.fillStyle = "rgba(15,18,30,0.82)";
      ctx.fillRect(sx - w / 2, sy - h / 2, w, h);
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(str, sx, sy);
    }

    // ── Segment canvas ────────────────────────────────────
    function dimSeg(ctx, x1, y1, x2, y2) {
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    }

    // ── Dessin d'une cotation ─────────────────────────────
    function drawOneDimension(ctx, dim) {
      // Utilise toujours le style courant (live) pour que les modifs Config soient immédiates
      const style  = dimStyleRef.current ?? dim.style;
      const z      = camera.zoom;
      const color  = style.color;
      const fontPx = Math.max(9, style.textSize * z);
      const arrPx  = Math.max(5, style.arrowSize * z);
      const gapW   = style.lineGap;
      const prec   = style.precision;
      const overW  = style.arrowSize * 0.4;
      const sw2    = (wx, wy) => worldToScreen(wx, wy);

      ctx.save();
      ctx.strokeStyle = color;
      ctx.fillStyle   = color;
      ctx.lineWidth   = 1;

      switch (dim.type) {
        // ── LINÉAIRE (H ou V) ───────────────────────────────
        case "dim_linear": {
          const { p1, p2, horiz, dimLinePos } = dim;
          if (horiz) {
            const lp = p1.x <= p2.x ? p1 : p2, rp = p1.x <= p2.x ? p2 : p1;
            const sign = dimLinePos >= lp.y ? 1 : -1;
            const e1s = sw2(lp.x, lp.y + sign * gapW), e1e = sw2(lp.x, dimLinePos + sign * overW);
            const e2s = sw2(rp.x, rp.y + sign * gapW), e2e = sw2(rp.x, dimLinePos + sign * overW);
            dimSeg(ctx, e1s.x, e1s.y, e1e.x, e1e.y);
            dimSeg(ctx, e2s.x, e2s.y, e2e.x, e2e.y);
            const d1 = sw2(lp.x, dimLinePos), d2 = sw2(rp.x, dimLinePos);
            dimSeg(ctx, d1.x, d1.y, d2.x, d2.y);
            const dx = d2.x - d1.x, dy = d2.y - d1.y, dl = Math.hypot(dx, dy);
            if (dl > 1) {
              drawDimArrow(ctx, d1.x, d1.y,  dx/dl,  dy/dl, arrPx);
              drawDimArrow(ctx, d2.x, d2.y, -dx/dl, -dy/dl, arrPx);
            }
            const mid = sw2((lp.x + rp.x) / 2, dimLinePos);
            drawDimLabel(ctx, Math.abs(p2.x - p1.x).toFixed(prec), mid.x, mid.y, color, fontPx);
          } else {
            const bp = p1.y <= p2.y ? p1 : p2, tp = p1.y <= p2.y ? p2 : p1;
            const sign = dimLinePos >= bp.x ? 1 : -1;
            const e1s = sw2(bp.x + sign * gapW, bp.y), e1e = sw2(dimLinePos + sign * overW, bp.y);
            const e2s = sw2(tp.x + sign * gapW, tp.y), e2e = sw2(dimLinePos + sign * overW, tp.y);
            dimSeg(ctx, e1s.x, e1s.y, e1e.x, e1e.y);
            dimSeg(ctx, e2s.x, e2s.y, e2e.x, e2e.y);
            const d1 = sw2(dimLinePos, bp.y), d2 = sw2(dimLinePos, tp.y);
            dimSeg(ctx, d1.x, d1.y, d2.x, d2.y);
            const dx = d2.x - d1.x, dy = d2.y - d1.y, dl = Math.hypot(dx, dy);
            if (dl > 1) {
              drawDimArrow(ctx, d1.x, d1.y,  dx/dl,  dy/dl, arrPx);
              drawDimArrow(ctx, d2.x, d2.y, -dx/dl, -dy/dl, arrPx);
            }
            const mid = sw2(dimLinePos, (bp.y + tp.y) / 2);
            ctx.save();
            ctx.translate(mid.x, mid.y);
            ctx.rotate(-Math.PI / 2);
            drawDimLabel(ctx, Math.abs(p2.y - p1.y).toFixed(prec), 0, 0, color, fontPx);
            ctx.restore();
          }
          break;
        }

        // ── ALIGNÉE ─────────────────────────────────────────
        case "dim_aligned": {
          const { p1, p2, perpDist } = dim;
          const dxW = p2.x - p1.x, dyW = p2.y - p1.y;
          const lenW = Math.hypot(dxW, dyW);
          if (lenW < 1e-9) break;
          const tx = dxW / lenW, ty = dyW / lenW;
          const nx = -ty, ny = tx;
          const sign = perpDist >= 0 ? 1 : -1;
          const d1w = { x: p1.x + nx * perpDist, y: p1.y + ny * perpDist };
          const d2w = { x: p2.x + nx * perpDist, y: p2.y + ny * perpDist };
          const e1s = sw2(p1.x + nx * sign * gapW, p1.y + ny * sign * gapW);
          const e1e = sw2(d1w.x + nx * sign * overW, d1w.y + ny * sign * overW);
          const e2s = sw2(p2.x + nx * sign * gapW, p2.y + ny * sign * gapW);
          const e2e = sw2(d2w.x + nx * sign * overW, d2w.y + ny * sign * overW);
          dimSeg(ctx, e1s.x, e1s.y, e1e.x, e1e.y);
          dimSeg(ctx, e2s.x, e2s.y, e2e.x, e2e.y);
          const dl1 = sw2(d1w.x, d1w.y), dl2 = sw2(d2w.x, d2w.y);
          dimSeg(ctx, dl1.x, dl1.y, dl2.x, dl2.y);
          const ddx = dl2.x - dl1.x, ddy = dl2.y - dl1.y, dl = Math.hypot(ddx, ddy);
          if (dl > 1) {
            drawDimArrow(ctx, dl1.x, dl1.y,  ddx/dl,  ddy/dl, arrPx);
            drawDimArrow(ctx, dl2.x, dl2.y, -ddx/dl, -ddy/dl, arrPx);
          }
          const midW = { x: (d1w.x + d2w.x) / 2, y: (d1w.y + d2w.y) / 2 };
          const ms   = sw2(midW.x, midW.y);
          const ang  = Math.atan2(dl2.y - dl1.y, dl2.x - dl1.x);
          ctx.save();
          ctx.translate(ms.x, ms.y);
          ctx.rotate(ang);
          drawDimLabel(ctx, lenW.toFixed(prec), 0, 0, color, fontPx);
          ctx.restore();
          break;
        }

        // ── ANGULAIRE ────────────────────────────────────────
        case "dim_angular": {
          const { center, a1, a2, radius, ccw } = dim;
          const cs  = sw2(center.x, center.y);
          const rA1 = sw2(center.x + Math.cos(a1) * radius, center.y + Math.sin(a1) * radius);
          const rA2 = sw2(center.x + Math.cos(a2) * radius, center.y + Math.sin(a2) * radius);
          const scrR = Math.hypot(rA1.x - cs.x, rA1.y - cs.y);
          // Extension lines from vertex to arc endpoints
          const ex1 = sw2(center.x + Math.cos(a1) * (radius + overW), center.y + Math.sin(a1) * (radius + overW));
          const ex2 = sw2(center.x + Math.cos(a2) * (radius + overW), center.y + Math.sin(a2) * (radius + overW));
          dimSeg(ctx, cs.x, cs.y, ex1.x, ex1.y);
          dimSeg(ctx, cs.x, cs.y, ex2.x, ex2.y);
          // Arc
          const sa = Math.atan2(rA1.y - cs.y, rA1.x - cs.x);
          const ea = Math.atan2(rA2.y - cs.y, rA2.x - cs.x);
          ctx.beginPath();
          ctx.arc(cs.x, cs.y, scrR, sa, ea, !ccw);
          ctx.stroke();
          // Arrows (tangent at arc endpoints)
          const dt = 0.01;
          const at1p = sw2(center.x + Math.cos(a1 + dt) * radius, center.y + Math.sin(a1 + dt) * radius);
          const at1m = sw2(center.x + Math.cos(a1 - dt) * radius, center.y + Math.sin(a1 - dt) * radius);
          const at2p = sw2(center.x + Math.cos(a2 + dt) * radius, center.y + Math.sin(a2 + dt) * radius);
          const at2m = sw2(center.x + Math.cos(a2 - dt) * radius, center.y + Math.sin(a2 - dt) * radius);
          const a1dx = ccw ? at1p.x-at1m.x : at1m.x-at1p.x, a1dy = ccw ? at1p.y-at1m.y : at1m.y-at1p.y;
          const a1l  = Math.hypot(a1dx, a1dy);
          if (a1l > 0) drawDimArrow(ctx, rA1.x, rA1.y, a1dx/a1l, a1dy/a1l, arrPx);
          const a2dx = ccw ? at2m.x-at2p.x : at2p.x-at2m.x, a2dy = ccw ? at2m.y-at2p.y : at2p.y-at2m.y;
          const a2l  = Math.hypot(a2dx, a2dy);
          if (a2l > 0) drawDimArrow(ctx, rA2.x, rA2.y, a2dx/a2l, a2dy/a2l, arrPx);
          // Text at mid arc
          let sweep = a2 - a1;
          if (ccw) { while (sweep < 0) sweep += 2*Math.PI; } else { while (sweep > 0) sweep -= 2*Math.PI; }
          const midA = a1 + sweep * 0.5;
          const tm = sw2(center.x + Math.cos(midA) * radius, center.y + Math.sin(midA) * radius);
          drawDimLabel(ctx, Math.abs(sweep * 180 / Math.PI).toFixed(1) + "°", tm.x, tm.y, color, fontPx);
          break;
        }

        // ── RAYON ────────────────────────────────────────────
        case "dim_radial": {
          const { cx, cy, radius, textPt } = dim;
          const ddx = textPt.x - cx, ddy = textPt.y - cy;
          const dlen = Math.hypot(ddx, ddy);
          if (dlen < 1e-9) break;
          const ndx = ddx / dlen, ndy = ddy / dlen;
          const ep = sw2(cx + ndx * radius, cy + ndy * radius);
          const ts = sw2(textPt.x, textPt.y);
          dimSeg(ctx, ep.x, ep.y, ts.x, ts.y);
          const edir = { x: ts.x - ep.x, y: ts.y - ep.y }, elen = Math.hypot(ts.x - ep.x, ts.y - ep.y);
          if (elen > 0) drawDimArrow(ctx, ep.x, ep.y, edir.x/elen, edir.y/elen, arrPx);
          drawDimLabel(ctx, `R ${radius.toFixed(prec)}`, ts.x, ts.y, color, fontPx);
          break;
        }

        // ── DIAMÈTRE ─────────────────────────────────────────
        case "dim_diameter": {
          const { cx, cy, radius, textPt } = dim;
          const ddx = textPt.x - cx, ddy = textPt.y - cy;
          const dlen = Math.hypot(ddx, ddy);
          if (dlen < 1e-9) break;
          const ndx = ddx / dlen, ndy = ddy / dlen;
          const ep1 = sw2(cx + ndx * radius, cy + ndy * radius);
          const ep2 = sw2(cx - ndx * radius, cy - ndy * radius);
          const ts  = sw2(textPt.x, textPt.y);
          dimSeg(ctx, ep1.x, ep1.y, ep2.x, ep2.y);
          const ddxS = ep2.x - ep1.x, ddyS = ep2.y - ep1.y, dlS = Math.hypot(ddxS, ddyS);
          if (dlS > 0) {
            drawDimArrow(ctx, ep1.x, ep1.y, -ddxS/dlS, -ddyS/dlS, arrPx);
            drawDimArrow(ctx, ep2.x, ep2.y,  ddxS/dlS,  ddyS/dlS, arrPx);
          }
          dimSeg(ctx, ep1.x, ep1.y, ts.x, ts.y);
          drawDimLabel(ctx, `⌀ ${(radius * 2).toFixed(prec)}`, ts.x, ts.y, color, fontPx);
          break;
        }

        // ── LONGUEUR D'ARC ────────────────────────────────────
        case "dim_arc_length": {
          const { cx, cy, radius, startAngle, endAngle, textPt } = dim;
          let dA = endAngle - startAngle;
          while (dA < 0) dA += 2 * Math.PI;
          if (dA > 2 * Math.PI) dA -= 2 * Math.PI;
          const cs    = sw2(cx, cy);
          const rOut  = radius + overW * 2;
          const rPt1  = sw2(cx + Math.cos(startAngle) * rOut, cy + Math.sin(startAngle) * rOut);
          const rPt2  = sw2(cx + Math.cos(endAngle)   * rOut, cy + Math.sin(endAngle)   * rOut);
          const rPt1b = sw2(cx + Math.cos(startAngle) * radius, cy + Math.sin(startAngle) * radius);
          const rPt2b = sw2(cx + Math.cos(endAngle)   * radius, cy + Math.sin(endAngle)   * radius);
          dimSeg(ctx, rPt1b.x, rPt1b.y, rPt1.x, rPt1.y);
          dimSeg(ctx, rPt2b.x, rPt2b.y, rPt2.x, rPt2.y);
          const scrR1 = Math.hypot(rPt1.x - cs.x, rPt1.y - cs.y);
          ctx.beginPath();
          ctx.arc(cs.x, cs.y, scrR1,
            Math.atan2(rPt1.y - cs.y, rPt1.x - cs.x),
            Math.atan2(rPt2.y - cs.y, rPt2.x - cs.x), false);
          ctx.stroke();
          const midA = startAngle + dA / 2;
          const midPt = sw2(cx + Math.cos(midA) * rOut, cy + Math.sin(midA) * rOut);
          const ts    = sw2(textPt.x, textPt.y);
          dimSeg(ctx, midPt.x, midPt.y, ts.x, ts.y);
          drawDimLabel(ctx, `⌒ ${(radius * dA).toFixed(prec)}`, ts.x, ts.y, color, fontPx);
          break;
        }

        // ── ORDONNÉE ──────────────────────────────────────────
        case "dim_ordinate": {
          const { point, leaderEnd, axis } = dim;
          const ps = sw2(point.x, point.y), le = sw2(leaderEnd.x, leaderEnd.y);
          const mx = axis === "y" ? leaderEnd.x : point.x;
          const my = axis === "x" ? leaderEnd.y : point.y;
          const ms2 = sw2(mx, my);
          ctx.beginPath();
          ctx.moveTo(ps.x, ps.y); ctx.lineTo(ms2.x, ms2.y); ctx.lineTo(le.x, le.y);
          ctx.stroke();
          const val = axis === "x" ? point.x : point.y;
          drawDimLabel(ctx, `${axis.toUpperCase()}: ${val.toFixed(prec)}`, le.x, le.y, color, fontPx);
          break;
        }

        // ── INCLINÉE (jogged radial) ───────────────────────────
        case "dim_jogged": {
          const { cx, cy, radius, jogPt, textPt } = dim;
          const ddx = jogPt.x - cx, ddy = jogPt.y - cy;
          const dlen = Math.hypot(ddx, ddy);
          if (dlen < 1e-9) break;
          const ndx = ddx / dlen, ndy = ddy / dlen;
          const ep  = sw2(cx + ndx * radius, cy + ndy * radius);
          const jw  = sw2(jogPt.x, jogPt.y);
          const ts  = sw2(textPt.x, textPt.y);
          const edir = { x: jw.x - ep.x, y: jw.y - ep.y }, elen = Math.hypot(jw.x-ep.x, jw.y-ep.y);
          if (elen > 0) drawDimArrow(ctx, ep.x, ep.y, edir.x/elen, edir.y/elen, arrPx);
          const j1x = jw.x + (ts.x-jw.x)*0.3 + (jw.y-ts.y)*0.15;
          const j1y = jw.y + (ts.y-jw.y)*0.3 + (ts.x-jw.x)*0.15;
          const j2x = jw.x + (ts.x-jw.x)*0.7 - (jw.y-ts.y)*0.15;
          const j2y = jw.y + (ts.y-jw.y)*0.7 - (ts.x-jw.x)*0.15;
          ctx.beginPath();
          ctx.moveTo(ep.x, ep.y); ctx.lineTo(jw.x, jw.y);
          ctx.lineTo(j1x, j1y); ctx.lineTo(j2x, j2y); ctx.lineTo(ts.x, ts.y);
          ctx.stroke();
          drawDimLabel(ctx, `R ${radius.toFixed(prec)}`, ts.x, ts.y, color, fontPx);
          break;
        }

        default: break;
      }
      ctx.restore();
    }

    // ── Builder helpers (pour preview) ────────────────────
    function buildLinearDim(p1, p2, offsetPt, style) {
      const midX = (p1.x + p2.x) / 2, midY = (p1.y + p2.y) / 2;
      const horiz = Math.abs(offsetPt.y - midY) >= Math.abs(offsetPt.x - midX);
      return { type: "dim_linear", p1, p2, horiz,
        dimLinePos: horiz ? offsetPt.y : offsetPt.x, style };
    }

    function buildAlignedDim(p1, p2, offsetPt, style) {
      const dxW = p2.x - p1.x, dyW = p2.y - p1.y;
      const lenW = Math.hypot(dxW, dyW);
      if (lenW < 1e-9) return null;
      const nx = -(dyW / lenW), ny = dxW / lenW;
      const perpDist = (offsetPt.x - p1.x) * nx + (offsetPt.y - p1.y) * ny;
      return { type: "dim_aligned", p1, p2, perpDist, style };
    }

    // ── Toutes les cotations finalisées ───────────────────
    function drawDimensions(ctx) {
      dimObjectsRef.current.forEach(d => drawOneDimension(ctx, d));
    }

    // ── Preview rubber-band cotation ──────────────────────
    function drawDimPreview(ctx) {
      const tool    = selectedToolRef.current;
      if (!tool.startsWith("dim_")) return;
      const pts     = drawingPtsRef.current;
      const preview = previewPtRef.current;
      const style   = dimStyleRef.current;
      if (!style || !preview) return;

      ctx.save();
      ctx.strokeStyle = style.color;
      ctx.fillStyle   = style.color;
      ctx.lineWidth   = 1;
      ctx.setLineDash([5, 4]);
      const sw2 = (wx, wy) => worldToScreen(wx, wy);

      if ((tool === "dim_linear" || tool === "dim_aligned") && pts.length >= 1) {
        const p1 = pts[0];
        const p2 = pts.length >= 2 ? pts[1] : preview;
        const ps1 = sw2(p1.x, p1.y), ps2 = sw2(p2.x, p2.y);
        ctx.beginPath(); ctx.moveTo(ps1.x, ps1.y); ctx.lineTo(ps2.x, ps2.y); ctx.stroke();
        if (pts.length >= 2) {
          ctx.setLineDash([]);
          const fakeDim = tool === "dim_linear"
            ? buildLinearDim(p1, p2, preview, style)
            : buildAlignedDim(p1, p2, preview, style);
          if (fakeDim) drawOneDimension(ctx, fakeDim);
        }
      }

      if (tool === "dim_angular") {
        if (pts.length >= 1) {
          const cs = sw2(pts[0].x, pts[0].y), pv = sw2(preview.x, preview.y);
          ctx.beginPath(); ctx.moveTo(cs.x, cs.y); ctx.lineTo(pv.x, pv.y); ctx.stroke();
        }
        if (pts.length >= 2) {
          const [center, leg1] = pts;
          const cs = sw2(center.x, center.y), l2 = sw2(preview.x, preview.y);
          ctx.beginPath(); ctx.moveTo(cs.x, cs.y); ctx.lineTo(l2.x, l2.y); ctx.stroke();
          const a1 = Math.atan2(leg1.y - center.y, leg1.x - center.x);
          const r  = Math.min(Math.hypot(leg1.x-center.x, leg1.y-center.y),
                               Math.hypot(preview.x-center.x, preview.y-center.y)) * 0.5;
          const rPt = sw2(center.x + Math.cos(a1) * r, center.y + Math.sin(a1) * r);
          const scrR = Math.hypot(rPt.x - cs.x, rPt.y - cs.y);
          ctx.beginPath();
          ctx.arc(cs.x, cs.y, scrR,
            Math.atan2(rPt.y - cs.y, rPt.x - cs.x),
            Math.atan2(l2.y - cs.y, l2.x - cs.x), false);
          ctx.stroke();
        }
      }

      if ((tool === "dim_radial" || tool === "dim_diameter") && pts.length >= 1) {
        const arc = dimArcRef.current;
        if (arc) {
          const ddx = preview.x - arc.cx, ddy = preview.y - arc.cy;
          const dl = Math.hypot(ddx, ddy);
          if (dl > 1e-9) {
            const ep = sw2(arc.cx + (ddx/dl)*arc.radius, arc.cy + (ddy/dl)*arc.radius);
            const ts = sw2(preview.x, preview.y);
            ctx.beginPath(); ctx.moveTo(ep.x, ep.y); ctx.lineTo(ts.x, ts.y); ctx.stroke();
          }
        }
      }

      if (tool === "dim_arc_length" && pts.length >= 1) {
        const arc = dimArcRef.current;
        if (arc && arc.startAngle != null) {
          const cs  = sw2(arc.cx, arc.cy);
          const rP  = sw2(arc.cx + Math.cos(arc.startAngle)*arc.radius, arc.cy + Math.sin(arc.startAngle)*arc.radius);
          const scrR = Math.hypot(rP.x - cs.x, rP.y - cs.y);
          ctx.beginPath();
          ctx.arc(cs.x, cs.y, scrR,
            Math.atan2(rP.y - cs.y, rP.x - cs.x),
            Math.atan2(sw2(arc.cx+Math.cos(arc.endAngle)*arc.radius, arc.cy+Math.sin(arc.endAngle)*arc.radius).y - cs.y,
                       sw2(arc.cx+Math.cos(arc.endAngle)*arc.radius, arc.cy+Math.sin(arc.endAngle)*arc.radius).x - cs.x), false);
          ctx.stroke();
        }
      }

      if (tool === "dim_ordinate" && pts.length >= 1) {
        const ps = sw2(pts[0].x, pts[0].y), pv = sw2(preview.x, preview.y);
        ctx.beginPath(); ctx.moveTo(ps.x, ps.y); ctx.lineTo(pv.x, pv.y); ctx.stroke();
      }

      if (tool === "dim_jogged") {
        const arc = dimArcRef.current;
        if (arc && pts.length >= 1) {
          const ddx = preview.x - arc.cx, ddy = preview.y - arc.cy;
          const dl  = Math.hypot(ddx, ddy);
          if (dl > 1e-9) {
            const ep  = sw2(arc.cx + (ddx/dl)*arc.radius, arc.cy + (ddy/dl)*arc.radius);
            const pv  = sw2(preview.x, preview.y);
            ctx.beginPath(); ctx.moveTo(ep.x, ep.y); ctx.lineTo(pv.x, pv.y); ctx.stroke();
          }
        }
        if (arc && pts.length >= 2) {
          const jw = sw2(pts[1].x, pts[1].y), pv = sw2(preview.x, preview.y);
          ctx.beginPath(); ctx.moveTo(jw.x, jw.y); ctx.lineTo(pv.x, pv.y); ctx.stroke();
        }
      }

      ctx.restore();
    }

    // ── Gestion des clics cotation ─────────────────────────
    function handleDimClick(pt) {
      const tool  = selectedToolRef.current;
      const pts   = drawingPtsRef.current;
      const style = { ...dimStyleRef.current };

      if (tool === "dim_linear" || tool === "dim_aligned") {
        if (pts.length === 0) {
          drawingPtsRef.current = [pt]; isDrawingRef.current = true;
          setStatusMsg("COT. – Spécifier le deuxième point");
        } else if (pts.length === 1) {
          drawingPtsRef.current = [pts[0], pt];
          setStatusMsg("COT. – Cliquer pour placer la ligne de cote");
        } else {
          const dim = tool === "dim_linear"
            ? buildLinearDim(pts[0], pts[1], pt, style)
            : buildAlignedDim(pts[0], pts[1], pt, style);
          if (dim) dimObjectsRef.current = [...dimObjectsRef.current, dim];
          resetDrawState(); setSelectedTool("select");
        }
        return;
      }

      if (tool === "dim_angular") {
        if (pts.length === 0) {
          drawingPtsRef.current = [pt]; isDrawingRef.current = true;
          setStatusMsg("COT. ANGULAIRE – Cliquer le premier côté");
        } else if (pts.length === 1) {
          drawingPtsRef.current = [pts[0], pt];
          setStatusMsg("COT. ANGULAIRE – Cliquer le deuxième côté");
        } else {
          const [center, leg1] = pts;
          const a1 = Math.atan2(leg1.y - center.y, leg1.x - center.x);
          const a2 = Math.atan2(pt.y - center.y, pt.x - center.x);
          let sweep = a2 - a1; while (sweep < 0) sweep += 2 * Math.PI;
          const ccw = sweep <= Math.PI;
          const r   = Math.min(
            Math.hypot(leg1.x-center.x, leg1.y-center.y),
            Math.hypot(pt.x-center.x,   pt.y-center.y)) * 0.5;
          dimObjectsRef.current = [...dimObjectsRef.current, {
            type:"dim_angular", center, a1, a2, radius:r, ccw, style }];
          resetDrawState(); setSelectedTool("select");
        }
        return;
      }

      if (tool === "dim_radial" || tool === "dim_diameter") {
        if (pts.length === 0) {
          const arc = detectNearCircleArc(pt.x, pt.y);
          if (!arc) { setStatusMsg("Aucun cercle/arc détecté – cliquer plus près du contour"); return; }
          dimArcRef.current = arc; drawingPtsRef.current = [pt]; isDrawingRef.current = true;
          setStatusMsg("COT. – Cliquer pour placer le texte");
        } else {
          const arc = dimArcRef.current;
          if (!arc) return;
          dimObjectsRef.current = [...dimObjectsRef.current, {
            type: tool, cx: arc.cx, cy: arc.cy, radius: arc.radius, textPt: pt, style }];
          dimArcRef.current = null; resetDrawState(); setSelectedTool("select");
        }
        return;
      }

      if (tool === "dim_arc_length") {
        if (pts.length === 0) {
          const arc = detectNearCircleArc(pt.x, pt.y);
          if (!arc || arc.type !== "ARC") { setStatusMsg("Aucun arc détecté – cliquer sur un arc"); return; }
          dimArcRef.current = arc; drawingPtsRef.current = [pt]; isDrawingRef.current = true;
          setStatusMsg("LONG. D'ARC – Cliquer pour placer le texte");
        } else {
          const arc = dimArcRef.current;
          if (!arc) return;
          dimObjectsRef.current = [...dimObjectsRef.current, {
            type:"dim_arc_length", cx:arc.cx, cy:arc.cy, radius:arc.radius,
            startAngle:arc.startAngle, endAngle:arc.endAngle, textPt:pt, style }];
          dimArcRef.current = null; resetDrawState(); setSelectedTool("select");
        }
        return;
      }

      if (tool === "dim_ordinate") {
        if (pts.length === 0) {
          drawingPtsRef.current = [pt]; isDrawingRef.current = true;
          setStatusMsg("ORDONNÉE – Cliquer la direction du repère");
        } else {
          const p    = pts[0];
          const axis = Math.abs(pt.x - p.x) > Math.abs(pt.y - p.y) ? "y" : "x";
          dimObjectsRef.current = [...dimObjectsRef.current, {
            type:"dim_ordinate", point:p, leaderEnd:pt, axis, style }];
          resetDrawState(); setSelectedTool("select");
        }
        return;
      }

      if (tool === "dim_jogged") {
        if (pts.length === 0) {
          const arc = detectNearCircleArc(pt.x, pt.y);
          if (!arc) { setStatusMsg("Aucun cercle/arc détecté"); return; }
          dimArcRef.current = arc; drawingPtsRef.current = [pt]; isDrawingRef.current = true;
          setStatusMsg("COT. INCLINÉE – Cliquer le point de coude");
        } else if (pts.length === 1) {
          drawingPtsRef.current = [pts[0], pt];
          setStatusMsg("COT. INCLINÉE – Cliquer la position du texte");
        } else {
          const arc = dimArcRef.current;
          if (!arc) return;
          dimObjectsRef.current = [...dimObjectsRef.current, {
            type:"dim_jogged", cx:arc.cx, cy:arc.cy, radius:arc.radius,
            jogPt:pts[1], textPt:pt, style }];
          dimArcRef.current = null; resetDrawState(); setSelectedTool("select");
        }
        return;
      }
    }

    // ── RAYCASTER POUR LA SÉLECTION ────────────────────────
    const raycaster = new THREE.Raycaster();
    raycaster.params.Line = { threshold: 0.3 };
    raycaster.near = 0.1;
    raycaster.far  = 800;
    let lastHovered = null;

    // Fonction utilitaire NDC depuis l'event
    function getNDC(e) {
      const r = mount.getBoundingClientRect();
      return new THREE.Vector2(
        ((e.clientX - r.left) / r.width)  * 2 - 1,
        -((e.clientY - r.top)  / r.height) * 2 + 1
      );
    }

    // ── HOVER HIGHLIGHT ────────────────────────────────────
    function updateHover(ndc) {
      raycaster.setFromCamera(ndc, camera);
      const hits = raycaster.intersectObjects(scene.children, true);
      if (hits.length > 0) {
        const hov = hits[0].object;
        if (hov !== lastHovered) {
          // Restaurer l'ancien hover
          if (lastHovered?.material) {
            lastHovered.material.color.setHex(lastHovered.userData._origColor ?? 0xffffff);
          }
          // Appliquer le nouveau hover
          if (hov.material) {
            hov.userData._origColor = hov.material.color.getHex();
            hov.material.color.setHex(0x4499ff);
          }
          lastHovered = hov;
        }
        mount.style.cursor = "pointer";
      } else {
        if (lastHovered?.material) {
          lastHovered.material.color.setHex(lastHovered.userData._origColor ?? 0xffffff);
        }
        lastHovered = null;
        mount.style.cursor = "crosshair";
      }
    }

    // ── MOUSE MOVE ────────────────────────────────────────
    const onMouseMove = (e) => {
      // Coordonnées monde
      const worldPt = screenToWorld(e.clientX, e.clientY);
      if (worldPt && isFinite(worldPt.x)) {
        setCursor({ x: +worldPt.x.toFixed(3), y: +worldPt.y.toFixed(3) });

        // Accrochage
        const snap = findSnap(worldPt.x, worldPt.y);
        snapPtRef.current    = snap;
        previewPtRef.current = snap
          ? { x: snap.x, y: snap.y }
          : { x: worldPt.x, y: worldPt.y };

        if (snap) {
          const labels = { endpoint: "⬛ Point final", midpoint: "△ Milieu", intersection: "✕ Intersection", nearest: "○ Le plus proche" };
          setSnapLabel(labels[snap.type] || snap.type);
        } else {
          setSnapLabel("");
        }

        // INSERT bloc : déplacer le fantôme THREE.js
        if (selectedToolRef.current === "insert_block" && ghostInsert) {
          const pt = previewPtRef.current ?? { x: worldPt.x, y: worldPt.y };
          ghostInsert.position.set(pt.x, pt.y, 0);
        }

        // Hachure : preview de la zone survolée
        if (selectedToolRef.current === "hatch") {
          const candidates = getClosedPolygons().filter(({ polygon }) =>
            pointInPolygon(worldPt.x, worldPt.y, polygon)
          );
          if (candidates.length > 0) {
            candidates.sort((a, b) => a.area - b.area);
            hatchPreviewRef.current = candidates[0].polygon;
          } else {
            hatchPreviewRef.current = null;
          }
        }
      }

      // Pan Ctrl+LMB
      if (isPanCtrlRef.current) {
        const dx = e.clientX - panCtrlStartRef.current.x;
        const dy = e.clientY - panCtrlStartRef.current.y;
        camera.position.x -= dx / camera.zoom;
        camera.position.y += dy / camera.zoom;
        camera.updateProjectionMatrix();
        panCtrlStartRef.current = { x: e.clientX, y: e.clientY };
      }

      // Pan molette
      if (isPanMidRef.current) {
        const dx = e.clientX - panMidStartRef.current.x;
        const dy = e.clientY - panMidStartRef.current.y;
        camera.position.x -= dx / camera.zoom;
        camera.position.y += dy / camera.zoom;
        camera.updateProjectionMatrix();
        panMidStartRef.current = { x: e.clientX, y: e.clientY };
      }

      // Hover (mode sélection seulement)
      if (selectedToolRef.current === "select" && !isPanCtrlRef.current && !isPanMidRef.current) {
        updateHover(getNDC(e));
      }

      // Boîte de sélection
      if (selStartRef.current && selBoxElRef.current) {
        const x = Math.min(e.clientX, selStartRef.current.x);
        const y = Math.min(e.clientY, selStartRef.current.y);
        const w = Math.abs(e.clientX - selStartRef.current.x);
        const h = Math.abs(e.clientY - selStartRef.current.y);
        if (w > 3 || h > 3) isDraggingRef.current = true;
        // Droite→Gauche = croisement (vert pointillé), Gauche→Droite = fenêtre (bleu)
        const crossing = e.clientX < selStartRef.current.x;
        selBoxElRef.current.style.left   = x + "px";
        selBoxElRef.current.style.top    = y + "px";
        selBoxElRef.current.style.width  = w + "px";
        selBoxElRef.current.style.height = h + "px";
        selBoxElRef.current.style.border = crossing
          ? "1px dashed #00ff88" : "1px solid #4499ff";
        selBoxElRef.current.style.background = crossing
          ? "rgba(0,255,136,0.05)" : "rgba(68,153,255,0.08)";
      }

      redrawOverlay();
    };

    // ── MOUSE DOWN ────────────────────────────────────────
    const onMouseDown = (e) => {
      // Molette → pan
      if (e.button === 1) {
        isPanMidRef.current    = true;
        panMidStartRef.current = { x: e.clientX, y: e.clientY };
        mount.style.cursor     = "grabbing";
        e.preventDefault();
        return;
      }
      // Ctrl + gauche → pan
      if (e.button === 0 && e.ctrlKey) {
        isPanCtrlRef.current    = true;
        panCtrlStartRef.current = { x: e.clientX, y: e.clientY };
        mount.style.cursor      = "grabbing";
        return;
      }
      // Gauche en mode sélection → démarrer boîte
      if (e.button === 0 && selectedToolRef.current === "select") {
        isDraggingRef.current = false;
        selStartRef.current   = { x: e.clientX, y: e.clientY };
        const box = document.createElement("div");
        box.style.cssText = `
          position: fixed;
          border: 1px solid #4499ff;
          background: rgba(68,153,255,0.08);
          pointer-events: none;
          left: ${e.clientX}px;
          top: ${e.clientY}px;
          width: 0; height: 0;
          z-index: 9998;
        `;
        document.body.appendChild(box);
        selBoxElRef.current = box;
      }
    };

    // ── MOUSE UP ─────────────────────────────────────────
    const onMouseUp = (e) => {
      // Fin pan molette
      if (e.button === 1) {
        isPanMidRef.current = false;
        mount.style.cursor  = "crosshair";
        return;
      }
      // Fin pan Ctrl
      if (isPanCtrlRef.current && e.button === 0) {
        isPanCtrlRef.current = false;
        mount.style.cursor   = "crosshair";
        return;
      }

      // Clic droit → annuler dessin ou désélectionner
      if (e.button === 2) {
        if (isDrawingRef.current) {
          cancelDrawing();
          const t = selectedToolRef.current;
          const initMsgs = {
            line:      "LIGNE – Spécifier le premier point",
            polyline:  "POLYLIGNE – Spécifier le premier point",
            rectangle: "RECTANGLE – Spécifier le premier coin",
          };
          setStatusMsg(initMsgs[t] || "Prêt");
        } else {
          // Désélectionner tout
          selectedObjsRef.current.forEach(obj => {
            if (obj.material && obj.userData._origColorSel !== undefined)
              obj.material.color.setHex(obj.userData._origColorSel);
          });
          selectedObjsRef.current = [];
          setSelectedEntities([]);
          setSelectedObject(null);
          setStatusMsg("Prêt - Cliquer pour sélectionner");
        }
        return;
      }

      // Bouton gauche relâché
      if (e.button === 0) {
        const wasDrawing = selectedToolRef.current !== "select";

        // ── Mode dessin : ajouter un point ──────────────
        if (wasDrawing && !isDraggingRef.current) {
          const tool = selectedToolRef.current;
          const pt   = snapPtRef.current
            ? { x: snapPtRef.current.x, y: snapPtRef.current.y }
            : previewPtRef.current;
          if (!pt) return;

          // LIGNE
          if (tool === "line") {
            if (!isDrawingRef.current) {
              drawingPtsRef.current = [{ x: pt.x, y: pt.y }];
              isDrawingRef.current  = true;
              setStatusMsg("LIGNE – Spécifier le point suivant (clic = valider, Echap = annuler)");
            } else {
              drawingPtsRef.current = [...drawingPtsRef.current, { x: pt.x, y: pt.y }];
              commitDrawing(); // → revient en select automatiquement
            }
            redrawOverlay();
            return;
          }

          // POLYLIGNE
          if (tool === "polyline") {
            if (!isDrawingRef.current) {
              drawingPtsRef.current = [{ x: pt.x, y: pt.y }];
              isDrawingRef.current  = true;
              setStatusMsg("POLYLIGNE – Point suivant (double-clic ou Entrée = terminer, Echap = annuler)");
            } else {
              drawingPtsRef.current = [...drawingPtsRef.current, { x: pt.x, y: pt.y }];
              setStatusMsg(`POLYLIGNE – ${drawingPtsRef.current.length} pts (double-clic = terminer)`);
            }
            redrawOverlay();
            return;
          }

          // CERCLE
          if (tool === "circle") {
            if (!isDrawingRef.current) {
              drawingPtsRef.current = [{ x: pt.x, y: pt.y }];
              isDrawingRef.current  = true;
              setStatusMsg("CERCLE – Spécifier un point sur le cercle (définit le rayon)");
            } else {
              drawingPtsRef.current = [...drawingPtsRef.current, { x: pt.x, y: pt.y }];
              commitCircle(); // → revient en select automatiquement
            }
            redrawOverlay();
            return;
          }

          // TEXTE
          if (tool === "text") {
            const rect = mount.getBoundingClientRect();
            setTextInput({
              sx: e.clientX - rect.left,
              sy: e.clientY - rect.top,
              wx: pt.x,
              wy: pt.y,
            });
            setTextValue("");
            return;
          }

          // RECTANGLE
          if (tool === "rectangle") {
            if (!isDrawingRef.current) {
              drawingPtsRef.current = [{ x: pt.x, y: pt.y }];
              isDrawingRef.current  = true;
              setStatusMsg("RECTANGLE – Spécifier le coin opposé (clic = valider, Echap = annuler)");
            } else {
              drawingPtsRef.current = [...drawingPtsRef.current, { x: pt.x, y: pt.y }];
              commitRectangle();
            }
            redrawOverlay();
            return;
          }

          // HACHURE
          if (tool === "hatch") {
            commitHatch(pt.x, pt.y);
            redrawOverlay();
            return;
          }

          // INSERT BLOC
          if (tool === "insert_block") {
            commitInsertBlock(pt.x, pt.y);
            return;
          }

          // COTATION
          if (tool.startsWith("dim_")) {
            handleDimClick(pt);
            redrawOverlay();
            return;
          }

          return;
        }

        // ── Mode sélection ───────────────────────────────
        if (selBoxElRef.current) {
          const endX = e.clientX, endY = e.clientY;

          if (isDraggingRef.current) {
            // Sélection par boîte
            const r        = mount.getBoundingClientRect();
            const crossing = endX < selStartRef.current.x; // D→G
            const minXs = Math.min(selStartRef.current.x, endX);
            const maxXs = Math.max(selStartRef.current.x, endX);
            const minYs = Math.min(selStartRef.current.y, endY);
            const maxYs = Math.max(selStartRef.current.y, endY);

            // Restaurer ancienne sélection
            selectedObjsRef.current.forEach(obj => {
              if (obj.material && obj.userData._origColorSel !== undefined)
                obj.material.color.setHex(obj.userData._origColorSel);
            });

            const newSel = [];
            scene.traverse((obj) => {
              const pos = obj.geometry?.attributes?.position;
              if (!pos) return;
              const n = pos.count;
              let anyIn = false, allIn = true;

              for (let i = 0; i < n; i++) {
                const v  = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i)).project(camera);
                const sx = (v.x * 0.5 + 0.5) * r.width  + r.left;
                const sy = (-v.y * 0.5 + 0.5) * r.height + r.top;
                const inside = sx >= minXs && sx <= maxXs && sy >= minYs && sy <= maxYs;
                if (inside) anyIn = true; else allIn = false;
              }

              const include = crossing ? anyIn : (allIn && n > 0);
              if (include) {
                newSel.push(obj);
                if (obj.material) {
                  if (obj.userData._origColorSel === undefined)
                    obj.userData._origColorSel = obj.material.color.getHex();
                  obj.material.color.setHex(0x00aaff);
                }
              }
            });

            selectedObjsRef.current = newSel;
            setSelectedEntities(newSel);
            setStatusMsg(`${newSel.length} objet(s) sélectionné(s)`);
          } else {
            // Simple clic → sélection unitaire
            // Restaurer ancienne sélection
            selectedObjsRef.current.forEach(obj => {
              if (obj.material && obj.userData._origColorSel !== undefined)
                obj.material.color.setHex(obj.userData._origColorSel);
            });

            raycaster.setFromCamera(getNDC(e), camera);
            const hits = raycaster.intersectObjects(scene.children, true);
            if (hits.length > 0) {
              // Cherche un ancêtre INSERT pour sélectionner le bloc entier
              const hitObj = hits[0].object;
              const insertGrp = findInsertAncestor(hitObj);
              const target    = insertGrp || hitObj;

              // Collecter tous les objets colorables du bloc (ou l'objet seul)
              const colorables = [];
              if (insertGrp) {
                insertGrp.traverse(child => {
                  if (child !== insertGrp && child.material) colorables.push(child);
                });
              } else if (target.material) {
                colorables.push(target);
              }

              colorables.forEach(obj => {
                obj.userData._origColorSel = obj.material.color.getHex();
                obj.material.color.setHex(0x00aaff);
              });
              selectedObjsRef.current = colorables;

              const ud = target.userData;
              const selData = {
                type: ud.type, layer: ud.layer, name: ud.name,
                _threeObj: target,
                ...(ud.type === "INSERT" ? {
                  position: [+target.position.x.toFixed(3), +target.position.y.toFixed(3)],
                  rotation: +THREE.MathUtils.radToDeg(target.rotation.z).toFixed(2),
                  scale:    [+target.scale.x.toFixed(3), +target.scale.y.toFixed(3)],
                } : {}),
              };
              setSelectedObject(selData);
              setSelectedEntities(colorables);
              setStatusMsg(`Sélectionné : ${ud.type === "INSERT" ? `BLOC [${ud.name}]` : (ud.type || "objet")} – calque: ${ud.layer || "?"}`);
            } else {
              selectedObjsRef.current = [];
              setSelectedObject(null);
              setSelectedEntities([]);
              setStatusMsg("Prêt - Cliquer pour sélectionner");
            }
          }

          document.body.removeChild(selBoxElRef.current);
          selBoxElRef.current = null;
          selStartRef.current = null;
          isDraggingRef.current = false;
        }
      }
    };

    // ── DOUBLE-CLIC → terminer polyligne ─────────────────
    const onDblClick = (e) => {
      if (e.button !== 0) return;
      const tool = selectedToolRef.current;
      if (tool === "polyline" && isDrawingRef.current && drawingPtsRef.current.length > 1) {
        commitDrawing(); // commitDrawing appelle setSelectedTool("select")
      }
    };

    // ── TOUCHES CLAVIER ───────────────────────────────────
    const onKeyDown = (e) => {
      // Supprimer la sélection (Delete ou Backspace) — sauf si un input est focalisé
      if ((e.key === "Delete" || e.key === "Backspace") &&
          !["INPUT","TEXTAREA","SELECT"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        deleteSelectedRef.current?.();
        return;
      }
      if (e.key === "Escape") {
        // Annuler insertion de bloc
        if (selectedToolRef.current === "insert_block") {
          removeBlockGhost();
          insertingBlockRef.current = null;
          setInsertingBlock(null);
          setSelectedTool("select");
          setStatusMsg("Prêt - Cliquer pour sélectionner");
          return;
        }
        if (isDrawingRef.current) {
          cancelDrawing();
          const t = selectedToolRef.current;
          const initMsgs = {
            line:      "LIGNE – Spécifier le premier point",
            polyline:  "POLYLIGNE – Spécifier le premier point",
            rectangle: "RECTANGLE – Spécifier le premier coin",
          };
          setStatusMsg(initMsgs[t] || "Prêt");
        }
      }
      if (e.key === "Enter") {
        const t = selectedToolRef.current;
        if (t === "polyline" && isDrawingRef.current && drawingPtsRef.current.length > 1) {
          commitDrawing(); // → revient en select
        }
      }
      // Ctrl+Z : annuler dernier point polyligne
      if (e.ctrlKey && e.key === "z") {
        if (isDrawingRef.current && drawingPtsRef.current.length > 1) {
          drawingPtsRef.current = drawingPtsRef.current.slice(0, -1);
          setStatusMsg(`POLYLIGNE – ${drawingPtsRef.current.length} pts`);
          redrawOverlay();
        }
      }
    };

    // ── MOLETTE → ZOOM ────────────────────────────────────
    const onWheel = (e) => {
      e.preventDefault();
      const factor = 1 - e.deltaY * 0.001;
      camera.zoom = Math.max(0.0001, Math.min(100000, camera.zoom * factor));
      camera.updateProjectionMatrix();
      setZoom(camera.zoom);
      redrawOverlay();
    };

    // ── REDIMENSIONNEMENT ─────────────────────────────────
    const onResize = () => {
      const w = mount.clientWidth, h = mount.clientHeight;
      camera.left = w / -2; camera.right = w / 2;
      camera.top  = h / 2;  camera.bottom = h / -2;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      overlay.width  = w;
      overlay.height = h;
      redrawOverlay();
    };

    // ── ANIMATION ─────────────────────────────────────────
    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      renderer.render(scene, camera);
      redrawOverlay(); // grille + snap + rubber-band + textes, chaque frame
    };
    animate();

    // ── ENREGISTREMENT DES EVENTS ─────────────────────────
    mount.addEventListener("mousedown", onMouseDown);
    mount.addEventListener("dblclick",  onDblClick);
    mount.addEventListener("wheel",     onWheel, { passive: false });
    mount.addEventListener("contextmenu", (e) => e.preventDefault());
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",   onMouseUp);
    window.addEventListener("keydown",   onKeyDown);
    window.addEventListener("resize",    onResize);

    // ── NETTOYAGE ─────────────────────────────────────────
    return () => {
      removeBlockGhost();
      cancelAnimationFrame(animId);
      mount.removeEventListener("mousedown", onMouseDown);
      mount.removeEventListener("dblclick",  onDblClick);
      mount.removeEventListener("wheel",     onWheel);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup",   onMouseUp);
      window.removeEventListener("keydown",   onKeyDown);
      window.removeEventListener("resize",    onResize);
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []); // ← UNE SEULE FOIS

  // =========================================================
  //  ZOOM via slider/boutons
  // =========================================================
  useEffect(() => {
    const cam = cameraRef.current;
    if (!cam) return;
    cam.zoom = zoom;
    cam.updateProjectionMatrix();
  }, [zoom]);

  // =========================================================
  //  SUPPRIMER LA SÉLECTION  (Delete / bouton)
  // =========================================================
  const handleDeleteSelected = useCallback(() => {
    const objs = selectedObjsRef.current;
    if (!objs.length) return;
    const n = objs.length;
    objs.forEach(obj => {
      if (obj.parent) obj.parent.remove(obj);
      obj.geometry?.dispose();
      const m = obj.material;
      if (m) (Array.isArray(m) ? m : [m]).forEach(mat => mat.dispose());
    });
    selectedObjsRef.current = [];
    setSelectedEntities([]);
    setSelectedObject(null);
    setStatusMsg(`✓ ${n} objet${n > 1 ? "s" : ""} supprimé${n > 1 ? "s" : ""}`);
  }, []);

  // Sync ref exposé au handler clavier (closure stable dans useEffect)
  deleteSelectedRef.current = handleDeleteSelected;

  // =========================================================
  //  EXPORTER DXF  (génération client-side)
  // =========================================================
  const handleExportDXF = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) { setStatusMsg("Aucune scène à exporter."); return; }

    const dxfText  = generateDXF(scene, layersByNameRef.current);
    const baseName = loadedFileName.replace(/\.dxf$/i, "") || "dessin_madjeo";
    const filename = `${baseName}_export.dxf`;

    const blob = new Blob([dxfText], { type: "application/dxf" });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement("a"), { href: url, download: filename });
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
    setStatusMsg(`✓ Export DXF : ${filename}`);
  }, [loadedFileName]);

  // =========================================================
  //  TOGGLE CALQUE
  // =========================================================
  const toggleLayer = (layerName) => {
    setLayers((prev) =>
      prev.map((l) => {
        if (l.name !== layerName) return l;
        const visible = !l.visible;
        const lInfo = layersByNameRef.current[layerName];
        if (lInfo) lInfo.group.visible = visible;
        return { ...l, visible };
      })
    );
  };

  const moveViewTo = (x, y) => {
    const cam = cameraRef.current;
    if (!cam) return;
    cam.position.set(x, y, 500);
    cam.lookAt(x, y, 0);
    cam.updateProjectionMatrix();
  };

  const zoomIn  = () => { const c = cameraRef.current; if (c) { c.zoom *= 1.35; c.updateProjectionMatrix(); setZoom(c.zoom); } };
  const zoomOut = () => { const c = cameraRef.current; if (c) { c.zoom /= 1.35; c.updateProjectionMatrix(); setZoom(c.zoom); } };
  const zoomExtents = () => {
    const c = cameraRef.current;
    if (!c) return;
    const [ecx, ecy] = dxfCenterRef.current;
    c.zoom = 1;
    c.position.set(ecx, ecy, 500);
    c.lookAt(ecx, ecy, 0);
    c.updateProjectionMatrix();
    setZoom(1);
  };

  // =========================================================
  //  OUTILS DU MENU RADIAL
  // =========================================================
  // 7 outils → espacement uniforme 360/7 ≈ 51.4° (départ en haut = -90°)
  const DRAW_TOOLS = [
    { id: "select",    label: "Sélection",  icon: "↖",  angle:  -90.0 },
    { id: "line",      label: "Ligne",      icon: "╱",  angle:  -38.6 },
    { id: "polyline",  label: "Polyligne",  icon: "〽", angle:   12.9 },
    { id: "circle",    label: "Cercle",     icon: "○",  angle:   64.3 },
    { id: "rectangle", label: "Rectangle",  icon: "▭",  angle:  115.7 },
    { id: "hatch",     label: "Hachure",    icon: "▥",  angle:  167.1 },
    { id: "text",      label: "Texte",      icon: "T",  angle: -141.4 },
  ];

  const ACTIVE_ICONS = {
    select: "↖", line: "╱", polyline: "〽", circle: "○", rectangle: "▭", hatch: "▥", text: "T",
  };

  const isDrawTool = ["line","polyline","circle","rectangle","hatch","text"].includes(selectedTool);

  // Cotation et Blocs retirés de l'UI (logique conservée, intégration ultérieure)

  // =========================================================
  //  JSX
  // =========================================================
  return (
    <div style={{
      fontFamily: "Segoe UI, Arial, sans-serif",
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      background: "#1e1e1e",
      color: "#e0e0e0",
      userSelect: "none",
    }}>

      {dxfLoading && <DXFLoader />}

      {/* ════════════════════════════════════════
          BARRE DE TITRE
      ═══════════════════════════════════════ */}
      <div style={{
        background: "#2d2d30",
        padding: "4px 14px",
        fontSize: 12,
        color: "#bbb",
        borderBottom: "1px solid #111",
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexShrink: 0,
      }}>
        <span style={{ fontWeight: "bold", color: "#4a9eff", fontSize: 13 }}>MadJeo CAD</span>
        <span style={{ color: "#555" }}>|</span>
        <span style={{ color: hasFile ? "#ddd" : "#555", fontStyle: hasFile ? "normal" : "italic" }}>
          {hasFile ? loadedFileName : "Aucun fichier — glissez un .dxf ou utilisez Fichier › Importer"}
        </span>
      </div>

      {/* ════════════════════════════════════════
          BARRE DE MENUS
      ═══════════════════════════════════════ */}
      <div style={{
        display: "flex",
        background: "#2d2d30",
        borderBottom: "1px solid #111",
        padding: "1px 4px",
        fontSize: 13,
        flexShrink: 0,
        position: "relative",
        zIndex: 300,
      }}>
        {/* Fichier (avec dropdown) */}
        <div style={{ position: "relative" }}>
          <span
            className="acad-menu-item"
            onClick={() => setShowFileMenu(v => !v)}
          >
            Fichier
          </span>
          {showFileMenu && (
            <>
              <div
                style={{ position: "fixed", inset: 0, zIndex: 998 }}
                onClick={() => setShowFileMenu(false)}
              />
              <div style={{
                position: "absolute", top: "100%", left: 0,
                background: "#2d2d30",
                border: "1px solid #444",
                borderRadius: 4,
                minWidth: 200,
                boxShadow: "0 6px 20px rgba(0,0,0,0.7)",
                zIndex: 999,
              }}>
                <div className="acad-dropdown-item" onClick={() => { fileInputRef.current?.click(); setShowFileMenu(false); }}>
                  <span style={{ color: "#888", marginRight: 8 }}>📂</span> Importer DXF…
                </div>
                <div className="acad-dropdown-sep" />
                <div className="acad-dropdown-sep" />
                <div
                  className="acad-dropdown-item"
                  onClick={() => { if (!hasFile) return; handleExportDXF(); setShowFileMenu(false); }}
                  style={{ opacity: hasFile ? 1 : 0.45, cursor: hasFile ? "pointer" : "not-allowed" }}
                  title={hasFile ? "Exporter la scène en fichier DXF" : "Importez d'abord un fichier DXF"}
                >
                  <Download size={14} style={{ marginRight: 8, color: "#4a9eff" }} />
                  Exporter en DXF…
                </div>
              </div>
            </>
          )}
        </div>

        <span
          className="acad-menu-item"
          onClick={() => { if (hasFile) setShowLayers(v => !v); }}
          style={{ opacity: hasFile ? 1 : 0.45, cursor: hasFile ? "pointer" : "not-allowed" }}
          title={hasFile ? undefined : "Importez d'abord un fichier DXF"}
        >Calques</span>
        <span
          className="acad-menu-item"
          onClick={() => { if (hasFile) setShowXYSearch(v => !v); }}
          style={{ opacity: hasFile ? 1 : 0.45, cursor: hasFile ? "pointer" : "not-allowed" }}
          title={hasFile ? undefined : "Importez d'abord un fichier DXF"}
        >Naviguer</span>

        {/* ── Config (avec sous-menus Cotation / Hachure) ── */}
        <div style={{ position: "relative" }}>
          <span
            className={`acad-menu-item${showDimConfig ? " acad-menu-item--active" : ""}`}
            onClick={() => setShowDimConfig(v => !v)}
          >
            Config
          </span>
          {showDimConfig && (
            <>
              <div style={{ position: "fixed", inset: 0, zIndex: 998 }} onClick={() => setShowDimConfig(false)} />
              <div style={{
                position: "absolute", top: "100%", left: 0,
                background: "#252526", border: "1px solid #444", borderRadius: 4,
                minWidth: 270, boxShadow: "0 6px 20px rgba(0,0,0,0.7)",
                zIndex: 999, fontSize: 12, color: "#ccc", overflow: "hidden",
              }}>

                {/* Onglets */}
                <div style={{ display: "flex", borderBottom: "1px solid #444" }}>
                  {[["cotes","⊢ Cotation"],["hachure","▥ Hachure"]].map(([id, label]) => (
                    <button key={id} onClick={() => setConfigTab(id)} style={{
                      flex: 1, padding: "7px 0", fontSize: 12, fontWeight: 600,
                      background: configTab === id ? "#1e1e1e" : "transparent",
                      color: configTab === id ? "#4a9eff" : "#888",
                      border: "none", borderBottom: configTab === id ? "2px solid #4a9eff" : "2px solid transparent",
                      cursor: "pointer", transition: "color .15s",
                    }}>
                      {label}
                    </button>
                  ))}
                </div>

                <div style={{ padding: "10px 14px 12px" }}>

                  {/* ── Sous-menu Cotation ───────────────────── */}
                  {configTab === "cotes" && <>
                    <div style={{ fontWeight: "bold", marginBottom: 10, color: "#fff" }}>Style de cotation</div>

                    <label style={{ display:"flex", alignItems:"center", marginBottom:8, gap:8 }}>
                      <span style={{ width:118 }}>Couleur</span>
                      <input type="color" value={dimStyle.color}
                        onChange={e => setDimStyle(s => ({ ...s, color: e.target.value }))}
                        style={{ flex:1, height:26, cursor:"pointer" }} />
                    </label>

                    {[
                      { label:"Taille texte",  key:"textSize",  min:0.05, max:5,  step:0.05 },
                      { label:"Taille flèche", key:"arrowSize", min:0.05, max:3,  step:0.05 },
                      { label:"Écart ligne",   key:"lineGap",   min:0,    max:1,  step:0.01 },
                    ].map(({ label, key, min, max, step }) => (
                      <label key={key} style={{ display:"flex", alignItems:"center", marginBottom:8, gap:8 }}>
                        <span style={{ width:118 }}>{label}</span>
                        <input type="range" min={min} max={max} step={step} value={dimStyle[key]}
                          onChange={e => setDimStyle(s => ({ ...s, [key]: parseFloat(e.target.value) }))}
                          style={{ flex:1 }} />
                        <span style={{ width:34, textAlign:"right", color:"#4a9eff" }}>{dimStyle[key]}</span>
                      </label>
                    ))}

                    <label style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ width:118 }}>Précision</span>
                      <select value={dimStyle.precision}
                        onChange={e => setDimStyle(s => ({ ...s, precision: parseInt(e.target.value) }))}
                        style={{ flex:1, background:"#333", color:"#fff", border:"1px solid #555", borderRadius:3, padding:"2px 4px" }}>
                        {[0,1,2,3,4,5].map(n => <option key={n} value={n}>{n} déc.</option>)}
                      </select>
                    </label>
                  </>}

                  {/* ── Sous-menu Hachure ────────────────────── */}
                  {configTab === "hachure" && <>
                    <div style={{ fontWeight:"bold", marginBottom:10, color:"#fff" }}>Style de hachure</div>

                    {/* Sélecteur de motif */}
                    <div style={{ marginBottom:10 }}>
                      <div style={{ marginBottom:6, color:"#aaa" }}>Motif</div>
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:4 }}>
                        {[
                          { id:"lines",    label:"Lignes",   sym:"╱╱" },
                          { id:"cross",    label:"Croisé",   sym:"╳╳" },
                          { id:"square",   label:"Carré",    sym:"⊞⊞" },
                          { id:"diamond",  label:"Losange",  sym:"◇◇" },
                          { id:"dot",      label:"Points",   sym:"···" },
                          { id:"zigzag",   label:"Zigzag",   sym:"∿∿" },
                          { id:"honeycomb",label:"Nid d'ab.",sym:"⬡⬡" },
                          { id:"brick",    label:"Briques",  sym:"▭▭" },
                        ].map(({ id, label, sym }) => (
                          <button key={id} onClick={() => setHatchStyle(s => ({ ...s, pattern: id }))}
                            title={label}
                            style={{
                              padding:"5px 2px", fontSize:10, cursor:"pointer", borderRadius:3,
                              background: hatchStyle.pattern === id ? "rgba(0,168,255,0.2)" : "rgba(255,255,255,0.04)",
                              border: `1px solid ${hatchStyle.pattern === id ? "#4a9eff" : "#444"}`,
                              color: hatchStyle.pattern === id ? "#4a9eff" : "#aaa",
                              textAlign:"center", lineHeight:1.3,
                            }}>
                            <div style={{ fontSize:14 }}>{sym}</div>
                            <div>{label}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Angle — uniquement si motif basé sur des lignes */}
                    {["lines","cross","zigzag"].includes(hatchStyle.pattern) && (
                      <label style={{ display:"flex", alignItems:"center", marginBottom:8, gap:8 }}>
                        <span style={{ width:118 }}>Angle (°)</span>
                        <input type="range" min={0} max={180} step={1} value={hatchStyle.angle}
                          onChange={e => setHatchStyle(s => ({ ...s, angle: parseFloat(e.target.value) }))}
                          style={{ flex:1 }} />
                        <span style={{ width:34, textAlign:"right", color:"#4a9eff" }}>{hatchStyle.angle}°</span>
                      </label>
                    )}

                    <label style={{ display:"flex", alignItems:"center", marginBottom:8, gap:8 }}>
                      <span style={{ width:118 }}>Espacement</span>
                      <input type="range" min={0.05} max={5} step={0.05} value={hatchStyle.spacing}
                        onChange={e => setHatchStyle(s => ({ ...s, spacing: parseFloat(e.target.value) }))}
                        style={{ flex:1 }} />
                      <span style={{ width:34, textAlign:"right", color:"#4a9eff" }}>{hatchStyle.spacing}</span>
                    </label>

                    <label style={{ display:"flex", alignItems:"center", marginBottom:8, gap:8 }}>
                      <span style={{ width:118 }}>Opacité</span>
                      <input type="range" min={0.05} max={1} step={0.05} value={hatchStyle.opacity}
                        onChange={e => setHatchStyle(s => ({ ...s, opacity: parseFloat(e.target.value) }))}
                        style={{ flex:1 }} />
                      <span style={{ width:34, textAlign:"right", color:"#4a9eff" }}>{hatchStyle.opacity}</span>
                    </label>

                    <label style={{ display:"flex", alignItems:"center", marginBottom:8, gap:8 }}>
                      <span style={{ width:118 }}>Couleur calque</span>
                      <input type="checkbox" checked={hatchStyle.useLayerColor}
                        onChange={e => setHatchStyle(s => ({ ...s, useLayerColor: e.target.checked }))}
                        style={{ cursor:"pointer" }} />
                    </label>

                    {!hatchStyle.useLayerColor && (
                      <label style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ width:118 }}>Couleur</span>
                        <input type="color" value={hatchStyle.color}
                          onChange={e => setHatchStyle(s => ({ ...s, color: e.target.value }))}
                          style={{ flex:1, height:26, cursor:"pointer" }} />
                      </label>
                    )}

                    <label style={{ display:"flex", alignItems:"center", marginTop:12, marginBottom:4, gap:8 }}>
                      <span style={{ width:118, color:"#aaa", fontSize:11 }}>Tol. jonction</span>
                      <input type="range" min={0.001} max={1} step={0.001} value={hatchStyle.snapTol}
                        onChange={e => setHatchStyle(s => ({ ...s, snapTol: parseFloat(e.target.value) }))}
                        style={{ flex:1 }} />
                      <span style={{ width:40, textAlign:"right", color:"#4a9eff", fontSize:11 }}>{hatchStyle.snapTol}</span>
                    </label>
                  </>}

                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Export PDF ── */}
        <span
          className={`acad-menu-item${showPdfPanel ? " acad-menu-item--active" : ""}`}
          onClick={() => { if (hasFile) setShowPdfPanel(v => !v); }}
          style={{ opacity: hasFile ? 1 : 0.45, cursor: hasFile ? "pointer" : "not-allowed" }}
          title={hasFile ? "Exporter en PDF à l'échelle" : "Importez d'abord un fichier DXF"}
        >
          Exporter PDF
        </span>

        <span className="acad-menu-item">Aide</span>

        <input
          ref={fileInputRef}
          type="file"
          accept=".dxf"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

      {/* ════════════════════════════════════════
          ZONE PRINCIPALE
      ═══════════════════════════════════════ */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>

        {/* Canvas Three.js — toujours monté pour que le contexte WebGL persiste */}
        <div
          ref={mountRef}
          style={{ width: "100%", height: "100%", cursor: "crosshair", display: "block" }}
        />

        {/* ════════════════════════════════════════
            DROP ZONE — affiché tant qu'aucun DXF n'est chargé
        ═══════════════════════════════════════ */}
        {!hasFile && (
          <div
            onDragOver={e => { e.preventDefault(); setDropActive(true); }}
            onDragLeave={() => setDropActive(false)}
            onDrop={handleDropZone}
            style={{
              position: "absolute", inset: 0, zIndex: 20,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: dropActive
                ? "rgba(10,14,28,0.97)"
                : "rgba(15,18,30,0.96)",
              transition: "background 0.18s",
            }}
          >
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              padding: "48px 56px",
              border: `2px dashed ${dropActive ? "#4a9eff" : "rgba(74,158,255,0.25)"}`,
              borderRadius: 18,
              background: dropActive ? "rgba(74,158,255,0.06)" : "rgba(255,255,255,0.02)",
              boxShadow: dropActive ? "0 0 40px rgba(74,158,255,0.12)" : "none",
              transition: "all 0.18s",
              maxWidth: 400,
              textAlign: "center",
            }}>
              {/* Icône DXF */}
              <div style={{
                width: 72, height: 72, borderRadius: 18,
                background: "rgba(74,158,255,0.1)",
                border: "1px solid rgba(74,158,255,0.25)",
                display: "flex", alignItems: "center", justifyContent: "center",
                marginBottom: 20,
                fontSize: 32,
                color: "#4a9eff",
                transition: "all 0.18s",
                boxShadow: dropActive ? "0 0 24px rgba(74,158,255,0.3)" : "none",
              }}>
                {dxfLoading ? (
                  <div style={{
                    width: 28, height: 28,
                    border: "3px solid rgba(74,158,255,0.2)",
                    borderTopColor: "#4a9eff",
                    borderRadius: "50%",
                    animation: "dxf-spin 0.65s linear infinite",
                  }} />
                ) : "⬚"}
              </div>

              <div style={{ fontSize: 17, fontWeight: 700, color: "#e0e0e0", marginBottom: 8, letterSpacing: "-0.02em" }}>
                {dxfLoading ? "Chargement en cours…" : dropActive ? "Relâchez pour importer" : "Glissez votre fichier DXF"}
              </div>
              <div style={{ fontSize: 13, color: "#555", marginBottom: 24, lineHeight: 1.6 }}>
                {!dxfLoading && "Déposez un fichier .dxf directement ici\nou cliquez sur le bouton ci-dessous"}
              </div>

              {!dxfLoading && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    padding: "10px 28px",
                    background: "linear-gradient(135deg, #1a4a8a, #2563a0)",
                    border: "1px solid rgba(74,158,255,0.4)",
                    color: "#c8e4ff",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    boxShadow: "0 4px 18px rgba(74,158,255,0.15)",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "linear-gradient(135deg,#1e5cb0,#2b78c0)"; e.currentTarget.style.boxShadow = "0 4px 24px rgba(74,158,255,0.3)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "linear-gradient(135deg,#1a4a8a,#2563a0)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(74,158,255,0.15)"; }}
                >
                  📂 Parcourir un fichier .dxf
                </button>
              )}

              <div style={{ fontSize: 11, color: "#333", marginTop: 20 }}>
                Formats acceptés : AutoCAD DXF (R2000–R2018)
              </div>
            </div>
          </div>
        )}

        {/* Canvas overlay (grille + accrochage + rubber-band + textes) */}
        <canvas
          ref={overlayRef}
          style={{
            position: "absolute", top: 0, left: 0,
            pointerEvents: "none",
            zIndex: 5,
          }}
        />

        {/* ════════════════════════════════════════
            SAISIE TEXTE FLOTTANTE (outil TEXTE)
        ═══════════════════════════════════════ */}
        {textInput && (
          <input
            type="text"
            autoFocus
            value={textValue}
            onChange={e => setTextValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && textValue.trim()) {
                // Ajouter le texte à la liste rendu overlay
                textObjectsRef.current = [
                  ...textObjectsRef.current,
                  { text: textValue.trim(), x: textInput.wx, y: textInput.wy, color: "#00aaff", size: 0.4 },
                ];
                setTextInput(null);
                setTextValue("");
                setSelectedTool("select");
              }
              if (e.key === "Escape") {
                setTextInput(null);
                setTextValue("");
                setSelectedTool("select");
              }
              e.stopPropagation(); // éviter que Echap déclenche cancelDrawing
            }}
            style={{
              position: "absolute",
              left: textInput.sx,
              top:  textInput.sy - 20,
              background: "rgba(15,18,30,0.95)",
              border: "1px solid #4a9eff",
              color: "#fff",
              fontFamily: "Consolas, monospace",
              fontSize: 14,
              padding: "3px 8px",
              borderRadius: 3,
              outline: "none",
              zIndex: 200,
              minWidth: 140,
              boxShadow: "0 2px 12px rgba(74,158,255,0.4)",
            }}
            placeholder="Texte… Entrée = valider"
          />
        )}

        {/* ════════════════════════════════════════
            BARRE D'OUTILS GAUCHE
        ═══════════════════════════════════════ */}
        <div style={{
          position: "absolute", top: 14, left: 14,
          display: "flex",
          flexDirection: "column",
          gap: 5,
          zIndex: 50,
        }}>
          {/* Sélection */}
          <button
            title="Sélection [S]"
            className={`acad-btn ${selectedTool === "select" ? "acad-btn--active" : ""}`}
            onClick={() => setSelectedTool("select")}
          >
            ↖
          </button>

          {/* Déclencheur menu radial dessin */}
          <button
            title={hasFile ? "Outils de dessin [D]" : "Importez d'abord un fichier DXF"}
            className={`acad-btn ${isDrawTool ? "acad-btn--draw" : ""}`}
            onClick={() => setShowRadialMenu(v => !v)}
            disabled={!hasFile}
          >
            {isDrawTool ? ACTIVE_ICONS[selectedTool] : "✏"}
          </button>

          <div className="acad-toolbar-sep" />

          <button title="Zoom +"  className="acad-btn" onClick={zoomIn}>+</button>
          <button title="Zoom −"  className="acad-btn" onClick={zoomOut}>−</button>
          <button title="Tout afficher" className="acad-btn acad-btn--sm" onClick={zoomExtents}>⊡</button>

          <div className="acad-toolbar-sep" />

          <button
            title={hasFile ? "Gestionnaire de calques" : "Importez d'abord un fichier DXF"}
            className={`acad-btn acad-btn--sm ${showLayers ? "acad-btn--active" : ""}`}
            onClick={() => setShowLayers(v => !v)}
            disabled={!hasFile}
          >
            ☰
          </button>

          <button
            title={hasFile ? "Aller aux coordonnées" : "Importez d'abord un fichier DXF"}
            className="acad-btn acad-btn--sm"
            style={{ fontSize: 11 }}
            onClick={() => setShowXYSearch(v => !v)}
            disabled={!hasFile}
          >
            XY
          </button>

          <div className="acad-toolbar-sep" />

          {/* ── Supprimer la sélection ── */}
          <button
            title={
              selectedEntities.length
                ? `Supprimer ${selectedEntities.length} objet${selectedEntities.length > 1 ? "s" : ""} [Suppr]`
                : "Aucun objet sélectionné"
            }
            className="acad-btn acad-btn--sm"
            onClick={handleDeleteSelected}
            disabled={selectedEntities.length === 0}
            style={{
              opacity: selectedEntities.length ? 1 : 0.3,
              cursor:  selectedEntities.length ? "pointer" : "default",
              background: selectedEntities.length ? "rgba(180,40,30,0.18)" : undefined,
              border:     selectedEntities.length ? "1px solid rgba(220,60,50,0.5)" : undefined,
              color:      selectedEntities.length ? "#e07060" : undefined,
            }}
          >
            <Trash2 size={16} />
          </button>

          {/* ── Exporter DXF ── */}
          <button
            title={hasFile ? "Exporter en DXF" : "Importez d'abord un fichier DXF"}
            className="acad-btn acad-btn--sm"
            onClick={handleExportDXF}
            disabled={!hasFile}
            style={{
              opacity: hasFile ? 1 : 0.3,
              cursor:  hasFile ? "pointer" : "default",
              background: hasFile ? "rgba(30,90,150,0.25)" : undefined,
              border:     hasFile ? "1px solid rgba(74,158,255,0.45)" : undefined,
              color:      hasFile ? "#4a9eff" : undefined,
            }}
          >
            <Download size={15} />
          </button>

        </div>

        {/* ════════════════════════════════════════
            MENU RADIAL DESSIN (style jeu)
        ═══════════════════════════════════════ */}
        {showRadialMenu && (
          <div style={{
            position: "absolute", inset: 0,
            zIndex: 200,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {/* Backdrop cliquable */}
            <div
              style={{
                position: "absolute", inset: 0,
                background: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(2px)",
              }}
              onClick={() => setShowRadialMenu(false)}
            />

            {/* Conteneur du menu */}
            <div style={{ position: "relative", width: 280, height: 280, flexShrink: 0 }}>
              {/* Cercle de fond */}
              <div style={{
                position: "absolute", top: "50%", left: "50%",
                transform: "translate(-50%,-50%)",
                width: 200, height: 200,
                borderRadius: "50%",
                border: "1px solid rgba(74,158,255,0.25)",
                background: "rgba(20,25,40,0.6)",
              }} />

              {/* Label central */}
              <div style={{
                position: "absolute", top: "50%", left: "50%",
                transform: "translate(-50%,-50%)",
                textAlign: "center",
                fontSize: 10, color: "#666",
                pointerEvents: "none",
              }}>
                DESSIN
              </div>

              {/* Boutons radiaux */}
              {DRAW_TOOLS.map((tool) => {
                const rad    = (tool.angle * Math.PI) / 180;
                const radius = 100;
                const cx     = 140, cy = 140;
                const x      = cx + Math.cos(rad) * radius - 28;
                const y      = cy + Math.sin(rad) * radius - 28;
                const active = selectedTool === tool.id;

                return (
                  <div
                    key={tool.id}
                    title={tool.label}
                    onClick={() => { setSelectedTool(tool.id); setShowRadialMenu(false); }}
                    className="radial-btn"
                    style={{
                      position: "absolute", left: x, top: y,
                      width: 56, height: 56,
                      borderRadius: "50%",
                      background: active
                        ? "rgba(0,90,200,0.95)"
                        : "rgba(30,38,60,0.95)",
                      border: `2px solid ${active ? "#4a9eff" : "#3a4a6a"}`,
                      boxShadow: active
                        ? "0 0 18px rgba(74,158,255,0.7)"
                        : "0 3px 10px rgba(0,0,0,0.6)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: active ? "#fff" : "#bbb",
                      transition: "transform 0.12s, box-shadow 0.12s",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = "scale(1.2)";
                      e.currentTarget.style.boxShadow = "0 0 18px rgba(74,158,255,0.8)";
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.boxShadow = active
                        ? "0 0 18px rgba(74,158,255,0.7)"
                        : "0 3px 10px rgba(0,0,0,0.6)";
                    }}
                  >
                    <span style={{ fontSize: 22, lineHeight: 1 }}>{tool.icon}</span>
                    <span style={{ fontSize: 9, marginTop: 3, color: "#8aaccc" }}>{tool.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {/* ════════════════════════════════════════
            PANNEAU CALQUES
        ═══════════════════════════════════════ */}
        {showLayers && (
          <div className="layers-panel-wrap" style={{
            position: "absolute", top: 14, right: 14,
            width: 270,
            maxHeight: 450,
            overflowY: "auto",
            background: "#252526",
            border: "1px solid #3c3c3c",
            borderRadius: 6,
            padding: "10px 12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.6)",
            zIndex: 100,
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
              borderBottom: "1px solid #3c3c3c",
              paddingBottom: 8,
            }}>
              <span style={{ fontWeight: "bold", fontSize: 13 }}>Calques</span>
              <button
                onClick={() => setShowLayers(false)}
                style={{ background: "transparent", border: "none", color: "#aaa", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
              >×</button>
            </div>

            {layers.map((l) => (
              <div
                key={l.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 3px",
                  borderRadius: 3,
                  background: activeLayer === l.name ? "rgba(74,158,255,0.12)" : "transparent",
                  gap: 7,
                  fontSize: 12,
                  cursor: "pointer",
                  marginBottom: 1,
                }}
                onClick={() => setActiveLayer(l.name)}
              >
                <input
                  type="checkbox"
                  checked={l.visible}
                  onChange={(e) => { e.stopPropagation(); toggleLayer(l.name); }}
                  style={{ margin: 0, cursor: "pointer" }}
                />
                <div style={{
                  width: 13, height: 13,
                  borderRadius: 2,
                  background: l.color_hex,
                  border: "1px solid rgba(255,255,255,0.15)",
                  flexShrink: 0,
                }} />
                <span style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: activeLayer === l.name ? "#4a9eff" : "#ccc",
                }} title={l.name}>
                  {l.name}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Fenêtre propriétés */}
        <PropertiesWindow
          selected={selectedObject}
          onClose={() => setSelectedObject(null)}
          onExplode={selectedObject?.type === "INSERT" ? () => {
            explodeInsertRef.current?.(selectedObject._threeObj);
          } : null}
        />

        {/* Fenêtre coordonnées */}
        {showXYSearch && (
          <XYSearchWindow
            onSearch={(x, y) => { moveViewTo(x, y); setShowXYSearch(false); }}
            onClose={() => setShowXYSearch(false)}
          />
        )}

        {/* ═══════════════════════════════════════
            PANNEAU BLOCS
        ═══════════════════════════════════════ */}
        {showBlockPanel && (
          <div style={{
            position: "absolute", top: 60, right: 12,
            width: 240, background: "#1e2533",
            border: "1px solid #3a4560", borderRadius: 6,
            boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
            zIndex: 1500, fontFamily: "Segoe UI, sans-serif",
            color: "#ccc", fontSize: 13,
          }}>
            {/* Entête */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "#2d3a55", padding: "7px 12px",
              borderBottom: "1px solid #3a4560", borderRadius: "6px 6px 0 0",
            }}>
              <span style={{ fontWeight: "bold", color: "#fff" }}>Blocs disponibles</span>
              <button onClick={() => setShowBlockPanel(false)}
                style={{ background: "transparent", border: "none", color: "#aaa", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>
                ×
              </button>
            </div>

            {/* Indicateur mode insertion actif */}
            {insertingBlock && (
              <div style={{ background: "#7a4fd6", padding: "6px 12px", fontSize: 12, color: "#fff" }}>
                Insertion : <b>{insertingBlock}</b> — Clic pour placer · ESC pour annuler
              </div>
            )}

            {/* Liste des blocs */}
            <div style={{ padding: "8px 0", maxHeight: 300, overflowY: "auto" }}>
              {blockNames.length === 0 && (
                <div style={{ padding: "8px 12px", color: "#666", fontStyle: "italic" }}>
                  Aucun bloc disponible.<br/>Chargez un DXF ou créez un bloc.
                </div>
              )}
              {blockNames.map(name => (
                <div key={name} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "5px 12px",
                  background: insertingBlock === name ? "rgba(122,79,214,0.2)" : "transparent",
                  borderLeft: insertingBlock === name ? "3px solid #7a4fd6" : "3px solid transparent",
                }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 16 }}>⬛</span>
                    <span style={{ color: insertingBlock === name ? "#b08fff" : "#ddd" }}>{name}</span>
                  </span>
                  <button
                    onClick={() => {
                      insertingBlockRef.current = name;
                      setInsertingBlock(name);
                      setSelectedTool("insert_block");
                      createBlockGhostRef.current?.(name);
                      setStatusMsg(`INSÉRER BLOC [${name}] – Cliquer pour placer · ESC pour annuler`);
                    }}
                    style={{
                      background: insertingBlock === name ? "#7a4fd6" : "#2d3a55",
                      border: `1px solid ${insertingBlock === name ? "#9a6ff6" : "#4a5a80"}`,
                      color: "#fff", borderRadius: 4, padding: "3px 8px",
                      cursor: "pointer", fontSize: 11,
                    }}>
                    ⊕ Insérer
                  </button>
                </div>
              ))}
            </div>

            {/* Créer bloc depuis sélection */}
            <div style={{ borderTop: "1px solid #3a4560", padding: "8px 12px" }}>
              <button
                disabled={selectedEntities.length === 0}
                onClick={() => { setNewBlockName(""); setCreateBlockOpen(true); }}
                style={{
                  width: "100%", padding: "6px 0",
                  background: selectedEntities.length > 0 ? "#2d4a2d" : "#222",
                  border: `1px solid ${selectedEntities.length > 0 ? "#4a8a4a" : "#444"}`,
                  color: selectedEntities.length > 0 ? "#8aff8a" : "#555",
                  borderRadius: 4, cursor: selectedEntities.length > 0 ? "pointer" : "default",
                  fontSize: 12,
                }}>
                ✚ Créer un bloc depuis la sélection
                {selectedEntities.length > 0 && <span style={{ opacity: 0.7 }}> ({selectedEntities.length} obj.)</span>}
              </button>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════
            MODAL CRÉER BLOC
        ═══════════════════════════════════════ */}
        {createBlockOpen && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            zIndex: 9000, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <div style={{
              background: "#1e2533", border: "1px solid #3a4560", borderRadius: 8,
              padding: "24px 28px", minWidth: 300, boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
              fontFamily: "Segoe UI, sans-serif", color: "#ccc",
            }}>
              <div style={{ fontWeight: "bold", fontSize: 15, color: "#fff", marginBottom: 14 }}>
                Créer un bloc
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", marginBottom: 5, fontSize: 12, color: "#aaa" }}>
                  Nom du bloc
                </label>
                <input
                  autoFocus
                  value={newBlockName}
                  onChange={e => setNewBlockName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && newBlockName.trim()) {
                      createBlockFromSelRef.current?.(newBlockName);
                      setCreateBlockOpen(false);
                    }
                    if (e.key === "Escape") setCreateBlockOpen(false);
                  }}
                  placeholder="Ex: PORTE, FENETRE…"
                  style={{
                    width: "100%", boxSizing: "border-box",
                    background: "#2d3a55", border: "1px solid #4a5a80",
                    color: "#fff", borderRadius: 4, padding: "7px 10px", fontSize: 13,
                    outline: "none",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button onClick={() => setCreateBlockOpen(false)}
                  style={{ padding: "6px 18px", background: "#333", border: "1px solid #555", color: "#aaa", borderRadius: 4, cursor: "pointer" }}>
                  Annuler
                </button>
                <button
                  disabled={!newBlockName.trim()}
                  onClick={() => {
                    createBlockFromSelRef.current?.(newBlockName);
                    setCreateBlockOpen(false);
                  }}
                  style={{
                    padding: "6px 18px",
                    background: newBlockName.trim() ? "#7a4fd6" : "#333",
                    border: `1px solid ${newBlockName.trim() ? "#9a6ff6" : "#555"}`,
                    color: newBlockName.trim() ? "#fff" : "#555",
                    borderRadius: 4, cursor: newBlockName.trim() ? "pointer" : "default",
                  }}>
                  Créer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════
            EXPORT PDF
        ═══════════════════════════════════════ */}
        {showPdfPanel && (
          <PdfExportPanel
            dxfData={dxfDataRef.current}
            boundingBox={dxfDataRef.current?.dxf_extent ?? null}
            sceneRef={sceneRef}
            onClose={() => setShowPdfPanel(false)}
          />
        )}

        {/* Badge accrochage actif */}
        {snapLabel && (
          <div style={{
            position: "absolute",
            top: 14,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(15,15,25,0.9)",
            border: "1px solid #ffff00",
            borderRadius: 4,
            padding: "3px 12px",
            fontSize: 11,
            color: "#ffff00",
            pointerEvents: "none",
            zIndex: 60,
            fontFamily: "monospace",
          }}>
            {snapLabel}
          </div>
        )}

        {/* Info-bulle commandes */}
        <div className="acad-hints" style={{
          position: "absolute",
          top: 14,
          right: showLayers ? 300 : 14,
          background: "rgba(15,15,25,0.75)",
          border: "1px solid #333",
          borderRadius: 4,
          padding: "4px 10px",
          fontSize: 10,
          color: "#777",
          pointerEvents: "none",
          zIndex: 50,
          lineHeight: 1.6,
        }}>
          <div>🖱 Molette = Zoom</div>
          <div>🖱 Molette enfoncée = Pan</div>
          <div>⌨ Ctrl+Glisser = Pan</div>
          <div>⌨ Echap = Annuler</div>
          <div>⌨ Entrée = Terminer</div>
          <div>⌨ Ctrl+Z = Annuler dernier pt</div>
        </div>
      </div>

      {/* ════════════════════════════════════════
          BARRE DE STATUT (AutoCAD style)
      ═══════════════════════════════════════ */}
      <div style={{
        background: "#007acc",
        padding: "3px 14px",
        fontSize: 11,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexShrink: 0,
        gap: 10,
      }}>
        <span style={{ color: "#fff", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {statusMsg}
        </span>
        <span className="acad-status-coords" style={{
          color: "#c8e8ff",
          fontFamily: "monospace",
          whiteSpace: "nowrap",
          fontSize: 11,
        }}>
          X: {cursor.x.toFixed(3)} &nbsp; Y: {cursor.y.toFixed(3)}
          &nbsp;|&nbsp; Calque: <strong>{activeLayer}</strong>
          &nbsp;|&nbsp; {selectedTool.toUpperCase()}
          &nbsp;|&nbsp; Objets: {selectedEntities.length}
        </span>
      </div>
    </div>
  );
}
